/* eslint-disable @typescript-eslint/no-unused-vars */
// NOTE: Some variables are unused after removing the old advanced shipping UI.
// They can be cleaned up in a later refactor.
import React, { useState, useEffect, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import { shippingApi, ordersApi, employeesApi, claimsApi, organizationApi, blockedAddressesApi } from '../services/api';
import { SplitQuantityModal } from './SplitQuantityModal';
import { UnifiedShippingView } from './UnifiedShippingView';
import { useToast } from './Toast';
import * as printerService from '../services/printer';
import { useEmployee } from '../contexts/EmployeeContext';
import { cleanupOldShippingDrafts, loadShippingDraft, removeShippingDraft, saveShippingDraft } from '../utils/shippingDraftStorage';
import { COUNTRIES, getRegionOptions, getCountryName, getPostalCodeLabel, getRegionLabel } from '../utils/addressData';
import {
  ArchiveBoxIcon,
  TruckIcon,
  GlobeAltIcon,
  StarIcon,
  CurrencyDollarIcon,
  GiftIcon,
  BoltIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
  ListBulletIcon,
  MapPinIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  ArrowUturnLeftIcon,
  ShieldExclamationIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';

interface OrderItem {
  id: string;
  title: string;
  sku?: string;
  quantity: number;
  price: number;
  product_title?: string;
  image_url?: string;
}

interface OrderDetails {
  id: string;
  order_number: string;
  first_name?: string;
  last_name?: string;
  customer_email?: string;
  total_price: number;
  status: string;
  created_at: string;
  shipping_address_line1?: string;
  shipping_address_line2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_zip?: string;
  shipping_country?: string;
  items?: OrderItem[];
  order_type?: 'shipping' | 'pickup';
  pickup_ready?: boolean;
  pickup_ready_at?: string;
  pickup_completed_at?: string;
  is_blocked?: boolean;
  blocked_reason?: string;
  blocked_address_id?: string;
  blocked_at?: string;
  customer_name?: string;
}

interface OrderDetailsModalProps {
  order: OrderDetails | null;
  onClose: () => void;
  onShip: () => void;
  initialTab?: 'summary' | 'ship' | 'shipments' | 'history';
}

// Helper function to format weight in lb oz
const formatWeight = (totalOz: number): string => {
  const pounds = Math.floor(totalOz / 16);
  const ounces = (totalOz % 16).toFixed(1);

  if (pounds === 0) {
    return `${totalOz.toFixed(1)} oz`;
  } else if (parseFloat(ounces) === 0) {
    return `${pounds} lb`;
  } else {
    return `${pounds} lb ${ounces} oz`;
  }
};

// Carrier logo component
const CarrierLogo: React.FC<{ carrier: string; size?: 'sm' | 'md' }> = ({ carrier, size = 'md' }) => {
  const carrierLower = carrier?.toLowerCase() || '';
  const sizeClass = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  
  if (carrierLower.includes('ups')) {
    return (
      <div className={`${sizeClass} bg-amber-100 rounded-lg flex items-center justify-center font-bold text-amber-800`}>
        UPS
      </div>
    );
  } else if (carrierLower.includes('usps')) {
    return (
      <div className={`${sizeClass} bg-blue-100 rounded-lg flex items-center justify-center font-bold text-blue-800`}>
        USPS
      </div>
    );
  } else if (carrierLower.includes('fedex')) {
    return (
      <div className={`${sizeClass} bg-purple-100 rounded-lg flex items-center justify-center font-bold text-purple-800`}>
        FDX
      </div>
    );
  } else if (carrierLower.includes('dhl')) {
    return (
      <div className={`${sizeClass} bg-yellow-100 rounded-lg flex items-center justify-center font-bold text-yellow-800`}>
        DHL
      </div>
    );
  }
  return (
    <div className={`${sizeClass} bg-slate-100 rounded-lg flex items-center justify-center font-bold text-slate-600`}>
      {carrier?.substring(0, 3).toUpperCase() || '?'}
    </div>
  );
};

// Helper function to detect format from base64 data
const detectLabelFormat = (base64: string): { contentType: string; extension: string } => {
  // Check base64 signature to detect format
  if (base64.startsWith('R0lGOD')) {
    return { contentType: 'image/gif', extension: 'gif' };
  } else if (base64.startsWith('iVBORw')) {
    return { contentType: 'image/png', extension: 'png' };
  } else if (base64.startsWith('JVBERi')) {
    return { contentType: 'application/pdf', extension: 'pdf' };
  }
  // Default to PDF
  return { contentType: 'application/pdf', extension: 'pdf' };
};

// Helper function to convert base64 string to Blob for downloading/printing
const base64ToBlob = (base64: string, contentType: string = 'application/pdf'): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

// Helper to open label in a new tab
const openLabel = (labelData?: string) => {
  if (!labelData) return;
  const format = detectLabelFormat(labelData);
  const blob = base64ToBlob(labelData, format.contentType);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
};

// Helper to download label as a file (works in async contexts where window.open is blocked)
const downloadLabel = (base64Data: string, filename: string) => {
  if (!base64Data) return;
  const format = detectLabelFormat(base64Data);
  const blob = base64ToBlob(base64Data, format.contentType);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.${format.extension}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Helper to open customs/commercial invoice documents for international packages
const openCustomsDocs = (packages: any[]) => {
  const urls = Array.from(
    new Set(
      (packages || [])
        .map((pkg: any) => pkg?.customs_form_url)
        .filter((url: string | undefined) => !!url)
    )
  ) as string[];

  if (!urls.length) return;
  urls.forEach((url) => window.open(url, '_blank', 'noopener,noreferrer'));
};

// Helper to print all labels in one document (multi-page)
const printAllLabels = async (packages: any[]) => {
  const labels = packages
    ?.map((pkg) => pkg.label_data)
    .filter(Boolean) as string[];

  if (!labels.length) return;

  const allPdfs = labels.every((labelData) => detectLabelFormat(labelData).contentType === 'application/pdf');

  if (allPdfs) {
    const merged = await PDFDocument.create();
    for (const labelData of labels) {
      const bytes = Uint8Array.from(atob(labelData), (c) => c.charCodeAt(0));
      const src = await PDFDocument.load(bytes);
      const pages = await merged.copyPages(src, src.getPageIndices());
      pages.forEach((p) => merged.addPage(p));
    }
    const mergedBytes = await merged.save();
    const blob = new Blob([mergedBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    return;
  }

  // Fallback: render images/mixed formats in a single HTML doc with page breaks
  const parts = labels.map((labelData, idx) => {
    const format = detectLabelFormat(labelData);
    return `
      <div class="page">
        <img src="data:${format.contentType};base64,${labelData}" style="max-width:100%; height:auto;" />
      </div>
    `;
  });

  const html = `
    <html>
      <head>
        <style>
          @media print { .page { page-break-after: always; } }
          body { margin: 0; padding: 0; }
          .page { padding: 8px; }
        </style>
      </head>
      <body>
        ${parts.join('')}
      </body>
    </html>
  `;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
};

interface Box {
  id: string;
  items: any[];
  weight: number;
  length: number;
  width: number;
  height: number;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  onClose,
  onShip,
  initialTab = 'ship'
}) => {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  // NOTE: Some variables below are unused after removing the old advanced shipping UI.
  // Keeping them for potential future use or can be cleaned up in a later refactor.
  /* eslint-enable @typescript-eslint/no-unused-vars */
  
  const toast = useToast();
  const { employee } = useEmployee();
  const [activeTab, setActiveTab] = useState<'summary' | 'ship' | 'shipments' | 'history'>(initialTab);

  // Log activity helper function
  const logActivity = async (actionType: string, description: string) => {
    if (!employee || !order) return;
    try {
      await employeesApi.logActivity({
        employee_id: employee.id,
        order_id: order.id,
        action_type: actionType,
        description,
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };
  const [shipments, setShipments] = useState<any[]>([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [creatingReturnLabel, setCreatingReturnLabel] = useState<string | null>(null);
  const [returnModalShipment, setReturnModalShipment] = useState<any>(null);
  const [returnItems, setReturnItems] = useState<Array<{ order_item_id: string; title: string; sku: string; quantity: number; maxQty: number; unitWeight: number; selected: boolean }>>([]);
  const [returnDims, setReturnDims] = useState({ length: 12, width: 10, height: 6 });
  const [returnWeightLb, setReturnWeightLb] = useState<number | null>(null);
  const [returnWeightOz, setReturnWeightOz] = useState<number | null>(null);
  const [maxPackageWeightOz, setMaxPackageWeightOz] = useState<number>(50 * 16);
  const [refreshedOrder, setRefreshedOrder] = useState<any>(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);
  
  // Claims state
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimShipment, setClaimShipment] = useState<any>(null);
  const [claimType, setClaimType] = useState('lost');
  const [claimDescription, setClaimDescription] = useState('');
  const [claimAmount, setClaimAmount] = useState('');
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [shipmentClaims, setShipmentClaims] = useState<Record<string, any>>({});
  const [isVerifyingAddress, setIsVerifyingAddress] = useState(false);
  const [editedAddress, setEditedAddress] = useState({
    line1: order?.shipping_address_line1 || '',
    line2: order?.shipping_address_line2 || '',
    city: order?.shipping_city || '',
    state: order?.shipping_state || '',
    zip: order?.shipping_zip || '',
    country: order?.shipping_country || 'US',
  });
  const [addressVerified, setAddressVerified] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [showManualShipment, setShowManualShipment] = useState(false);
  const [manualCarrier, setManualCarrier] = useState('UPS');
  const [manualTracking, setManualTracking] = useState('');
  const [manualCost, setManualCost] = useState<string>('');
  const [manualNotes, setManualNotes] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualIncludeItems, setManualIncludeItems] = useState(true);
  const [showManualInShipments, setShowManualInShipments] = useState(false);
  
  React.useEffect(() => {
    if (order?.id) {
      setActiveTab(initialTab || 'ship');
    }
  }, [order?.id, initialTab]);

  React.useEffect(() => {
    organizationApi.getSettings().then((s) => {
      if (s.max_package_weight_lbs) setMaxPackageWeightOz(s.max_package_weight_lbs * 16);
    }).catch(() => {});
  }, []);

  // Activity log state
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(true);
  const [weightWarnings, setWeightWarnings] = useState<any[]>([]);

  // Shipment state (from ShippingModal)
  const draftKey = `shipping_draft_${order?.id}`;
  const draft = order?.id ? loadShippingDraft(draftKey) : null;

  // Shrink legacy drafts that stored full label PDFs (can exceed localStorage quota)
  React.useEffect(() => {
    if (!order?.id || !draft) return;
    cleanupOldShippingDrafts(draftKey);
    saveShippingDraft(draftKey, draft as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // Use refreshed order data if available, otherwise use prop
  const currentOrder = refreshedOrder || order;
  const [derivedItems, setDerivedItems] = useState<any[]>([]);
  // items includes ALL items (even removed) - the backend now returns everything
  // The unfulfilledItems filter handles excluding fulfilled/removed for shipping
  const items = currentOrder?.items && currentOrder.items.length > 0
    ? currentOrder.items
    : derivedItems;

  // Build a last-known package weight fallback (only reliable if single item)
  const packageFallbackWeight = (() => {
    if (items.length !== 1 || shipments.length === 0) return null;
    const firstShipment = shipments.find((s: any) => s.status !== 'voided');
    if (!firstShipment) return null;
    const pkgWeight = firstShipment?.packages?.[0]?.weight;
    return pkgWeight ? parseFloat(pkgWeight) : null;
  })();

  const resolvedItems = (items || []).map((item: any) => {
    const variantW = item.variant_weight ? parseFloat(item.variant_weight) : 0;
    const productW = item.product_weight ? parseFloat(item.product_weight) : 0;
    const weightOz = item.weight_oz ? parseFloat(item.weight_oz) : 0;
    const qty = Math.max(1, parseFloat(String(item.quantity || 1)) || 1);
    const pkgWPerUnit = packageFallbackWeight ? packageFallbackWeight / qty : 0;
    const resolved =
      (variantW > 0 && variantW) ||
      (productW > 0 && productW) ||
      (weightOz > 0 && weightOz) ||
      (pkgWPerUnit > 0 && pkgWPerUnit) ||
      0;
    return {
      ...item,
      resolved_weight: resolved,
    };
  });

  // Debug: Log item data
  console.log('[OrderDetailsModal] Order items:', items.map((i: any) => ({
    title: i.title?.substring(0, 20),
    qty: i.quantity,
    fulfilled: i.fulfilled_quantity,
    unfulfilled: i.unfulfilled_quantity,
    variant_weight: i.variant_weight,
    product_weight: i.product_weight,
    has_weight: !!(i.variant_weight || i.product_weight),
  })));

  // Filter to only unfulfilled items (for partial fulfillments)
  const unfulfilledItems = resolvedItems
    .map((item: any) => {
      // Prefer API-provided unfulfilled_quantity
      let remaining =
        item.unfulfilled_quantity !== undefined
          ? parseInt(item.unfulfilled_quantity)
          : item.quantity - (item.fulfilled_quantity || 0);

      // If the order is shipped and the backend didn’t give unfulfilled counts,
      // treat items as fulfilled to avoid auto-dropping into configure flow.
      if (
        currentOrder?.status === 'shipped' &&
        (item.unfulfilled_quantity === undefined || isNaN(remaining) || remaining <= 0)
      ) {
        remaining = 0;
      }

      return {
        ...item,
        quantity: remaining > 0 ? remaining : 0,
        original_quantity: item.original_quantity || item.quantity,
        fulfilled_quantity: item.fulfilled_quantity || 0,
        _remaining: remaining,
      };
    })
    .filter((item: any) => item._remaining > 0);

  const [manualSkipFulfillment, setManualSkipFulfillment] = useState(unfulfilledItems.length === 0);
  const [reshipMode, setReshipMode] = useState(false);

  React.useEffect(() => {
    setManualIncludeItems(unfulfilledItems.length > 0);
    // Default to skip fulfillment when everything is already shipped (reship flow)
    setManualSkipFulfillment(unfulfilledItems.length === 0 || currentOrder?.status === 'shipped');
  }, [unfulfilledItems.length, currentOrder?.status]);

  // Always fetch fresh order data with items when modal opens
  React.useEffect(() => {
    const fetchOrderWithItems = async () => {
      if (order?.id) {
        try {
          const data = await ordersApi.get(order.id);
          setRefreshedOrder({
            ...data.order,
            items: data.items,
          });
          setWeightWarnings(data.weight_warnings || []);
        } catch (err) {
          console.error('[OrderDetailsModal] Failed to fetch items for order', order.id, err);
        }
      }
    };
    fetchOrderWithItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // If items are missing, proactively load shipments so we can derive items from packages
  React.useEffect(() => {
    if (!order?.id) return;
    const noItems = !currentOrder?.items || currentOrder.items.length === 0;
    if (noItems && shipments.length === 0 && !loadingShipments) {
      loadShipments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, currentOrder?.items?.length, shipments.length]);

  // Derive items from shipment packages when order items are empty (covers already-shipped orders)
  React.useEffect(() => {
    if (currentOrder?.items && currentOrder.items.length > 0) {
      setDerivedItems([]);
      return;
    }
    if (!shipments || shipments.length === 0) return;

    const aggregate: Record<string, any> = {};
    shipments.forEach((shipment: any) => {
      (shipment.packages || []).forEach((pkg: any) => {
        const pkgItems = pkg.items
          ? (typeof pkg.items === 'string' ? JSON.parse(pkg.items) : pkg.items)
          : [];
        pkgItems.forEach((itm: any) => {
          const key = itm.id || itm.order_item_id || itm.shopify_line_item_id || `${itm.title}-${itm.sku || ''}`;
          if (!aggregate[key]) {
            aggregate[key] = {
              id: key,
              title: itm.title,
              sku: itm.sku,
              quantity: 0,
              price: itm.price || 0,
              variant_title: itm.variant_title,
              image_url: itm.image_url,
            };
          }
          aggregate[key].quantity += itm.quantity || 0;
        });
      });
    });
    setDerivedItems(Object.values(aggregate));
  }, [shipments, currentOrder?.items]);

  console.log('Unfulfilled items count:', unfulfilledItems.length);

  // Detect if this is an international shipment
  const isInternational = currentOrder?.shipping_country && currentOrder.shipping_country !== 'US';
  
  // Check for items missing HS codes (from item data if available)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _itemsMissingHSCode = unfulfilledItems.filter((item: any) => !item.hs_tariff_code);

  const [step, setStep] = useState<'boxes' | 'rates' | 'labels' | 'confirm'>(draft?.step || 'boxes');

  // Initialize boxes - recalculate weights even if using draft (in case product data was updated)
  console.log('[BoxOps] === INITIALIZING BOXES ===');
  console.log('[BoxOps] Draft exists:', !!draft);
  console.log('[BoxOps] Draft boxes:', draft?.boxes ? JSON.stringify(draft.boxes.map((b: any) => ({ id: b.id, items: b.items?.map((i: any) => ({ id: i.id, qty: i.quantity })) }))) : 'none');
  console.log('[BoxOps] unfulfilledItems:', unfulfilledItems.map((i: any) => ({ id: i.id, title: i.title?.substring(0, 30), qty: i.quantity })));
  
  const initialBoxes = draft?.boxes || [
    {
      id: '1',
      items: unfulfilledItems.map((item: any) => ({ ...item, selected: true })),
      weight: unfulfilledItems.reduce((sum: number, item: any) => sum + (parseFloat((item as any).variant_weight || (item as any).product_weight || 0) * item.quantity), 0),
      length: 12,
      width: 12,
      height: 6,
    },
  ];

  // Recalculate weights for all boxes in case product weights changed
  // IMPORTANT: Refresh box items with current fresh data to get updated weights
  // BUT preserve the draft item's quantity (don't replace with fresh item's full quantity!)
  const boxesWithUpdatedWeights = initialBoxes.map((box: Box) => {
    // Replace draft items with fresh items that have current weights, but KEEP draft quantity
    const refreshedBoxItems = box.items.map((draftItem: any) => {
      const freshItem = unfulfilledItems.find((i: any) => i.id === draftItem.id);
      if (freshItem) {
        // Use fresh item data (weights, etc.) but PRESERVE the draft's quantity
        console.log('[BoxOps] Refresh item', draftItem.id, '- draftQty:', draftItem.quantity, 'freshQty:', freshItem.quantity, '-> using draftQty');
        return { ...freshItem, quantity: draftItem.quantity };
      }
      console.log('[BoxOps] Item', draftItem.id, 'not found in fresh items, keeping draft data');
      return draftItem; // Use draft data if fresh item not found
    });

    return {
      ...box,
      items: refreshedBoxItems,
      weight: refreshedBoxItems.reduce((sum: number, item: any) => {
        const itemWeight = parseFloat(item.variant_weight || item.product_weight || 0);
        return sum + (itemWeight * item.quantity);
      }, 0),
    };
  });
  console.log('[BoxOps] Final initialized boxes:', JSON.stringify(boxesWithUpdatedWeights.map((b: any) => ({ id: b.id, items: b.items.map((i: any) => ({ id: i.id, qty: i.quantity })), weight: b.weight }))));

  const [boxes, setBoxes] = useState<Box[]>(boxesWithUpdatedWeights);
  const [availableRates, setAvailableRates] = useState<any[]>(draft?.availableRates || []);
  const [selectedRate, setSelectedRate] = useState<any>(draft?.selectedRate || null);
  const [labels, setLabels] = useState<any[]>(draft?.labels || []);
  const labelsHydratedRef = useRef(false);

  // Restore label PDF data from server when draft only has metadata (drafts omit base64 to save space)
  React.useEffect(() => {
    labelsHydratedRef.current = false;
  }, [order?.id]);

  React.useEffect(() => {
    if (!order?.id || labelsHydratedRef.current) return;
    if (!labels.some((label) => label.shipment_id && !label.label_data)) return;

    labelsHydratedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const result = await shippingApi.getShipments(order.id);
        if (cancelled) return;

        const shipmentId = labels.find((label) => label.shipment_id)?.shipment_id;
        const shipment = (result.shipments || []).find((s: any) => s.id === shipmentId);
        if (!shipment?.packages?.length) return;

        setLabels((prev) =>
          prev.map((label) => {
            const pkg = shipment.packages.find(
              (p: any, idx: number) =>
                p.tracking_number === label.tracking || idx + 1 === label.box_number
            );
            if (!pkg?.label_data) return label;
            return {
              ...label,
              label_data: pkg.label_data,
              label_format: pkg.label_format || label.label_format,
            };
          })
        );
      } catch (err) {
        console.warn('[OrderDetailsModal] Failed to hydrate label data from server:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [order?.id, labels]);

  // Warn user if they try to close/refresh with unconfirmed labels
  const hasUnconfirmedLabels = labels.length > 0 && labels[0]?.shipment_id && labels[0]?.status !== 'confirmed';
  useEffect(() => {
    if (!hasUnconfirmedLabels) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnconfirmedLabels]);

  // Track if initial load is complete (to avoid saving incomplete state)
  const boxesInitializedRef = useRef(false);
  
  // Refs to always have latest state values for the visibility change handler
  const stateRef = useRef({ step, boxes, availableRates, selectedRate, labels });

  // Helper function to save draft to localStorage
  const saveDraftToStorage = React.useCallback(() => {
    if (!order?.id) return;
    
    const currentState = stateRef.current;
    const draftData = {
      step: currentState.step,
      boxes: currentState.boxes,
      availableRates: currentState.availableRates,
      selectedRate: currentState.selectedRate,
      labels: currentState.labels,
      // Insurance settings will be added by the effect
    };
    saveShippingDraft(draftKey, draftData);
    console.log('[BoxOps] Saved draft - step:', currentState.step, 'boxes:', currentState.boxes.map(b => ({ id: b.id, dims: `${b.length}x${b.width}x${b.height}` })));
  }, [order?.id, draftKey]);

  // Keep stateRef updated with latest values
  React.useEffect(() => {
    stateRef.current = { step, boxes, availableRates, selectedRate, labels };
  }, [step, boxes, availableRates, selectedRate, labels]);

  // Save draft when tab becomes hidden (user switches browser tabs)
  // This ensures we don't lose state even if React effects haven't run yet
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && order?.id) {
        console.log('[BoxOps] Tab hidden - saving draft immediately');
        saveDraftToStorage();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [order?.id, saveDraftToStorage]);

  // Auto-save draft when state changes
  // This ensures dimensions are preserved if the modal re-renders or user navigates
  React.useEffect(() => {
    if (!order?.id) return;
    
    // Skip first render to avoid saving incomplete initial state
    if (!boxesInitializedRef.current) {
      boxesInitializedRef.current = true;
      // But still save if we have meaningful state (e.g., loaded from previous draft)
      if (step !== 'boxes' || availableRates.length > 0) {
        saveDraftToStorage();
      }
      return;
    }
    
    saveDraftToStorage();
  }, [boxes, order?.id, step, availableRates, selectedRate, labels, saveDraftToStorage]);

  // Debug logging for box state changes
  React.useEffect(() => {
    console.log('[BoxOps] === BOX STATE CHANGED ===');
    console.log('[BoxOps] Total boxes:', boxes.length);
    boxes.forEach((box, idx) => {
      const totalQty = box.items.reduce((sum, i) => sum + i.quantity, 0);
      console.log(`[BoxOps] Box ${box.id}: ${box.items.length} items, totalQty=${totalQty}, weight=${box.weight}oz, dims=${box.length}x${box.width}x${box.height}`);
      box.items.forEach(item => {
        console.log(`[BoxOps]   - ${item.title?.substring(0, 40)} (id=${item.id}, qty=${item.quantity})`);
      });
    });
    // Check for duplicate items across boxes (sum quantities per item ID)
    const itemCounts: Record<string, number> = {};
    boxes.forEach(box => {
      box.items.forEach(item => {
        itemCounts[item.id] = (itemCounts[item.id] || 0) + item.quantity;
      });
    });
    console.log('[BoxOps] Item quantity totals across all boxes:', itemCounts);
  }, [boxes]);
  const [splittingItem, setSplittingItem] = useState<any>(null);
  const [creatingLabels, setCreatingLabels] = useState(false);
  const [labelFormat, setLabelFormat] = useState<'pdf' | 'png' | 'zpl'>('pdf');
  // Restore insurance settings from draft to avoid unnecessary rate recalculation on reopen
  const [insuranceOption, setInsuranceOption] = useState<'none' | 'carrier' | 'parcelguard'>(draft?.insuranceOption || 'none');
  const [insuranceAmount, setInsuranceAmount] = useState<number>(draft?.insuranceAmount || 0);
  const [ratesInsuranceAmount, setRatesInsuranceAmount] = useState<number>(draft?.ratesInsuranceAmount || 0); // Track insurance used when rates were fetched
  const [ratesInsuranceProvider, setRatesInsuranceProvider] = useState<'none' | 'carrier' | 'parcelguard'>(draft?.ratesInsuranceProvider || 'none'); // Track provider used
  const [signatureRequired, setSignatureRequired] = useState<'none' | 'signature' | 'adult_signature'>('none');
  const [methodMatch, setMethodMatch] = useState<{
    original_method?: string;
    suggested_rate_index?: number;
    confidence?: string;
    message?: string;
    requires_free_shipping?: boolean;
  } | null>(null);
  const [rateSortBy, setRateSortBy] = useState<'price' | 'speed'>('price');
  const [reference1, setReference1] = useState<string>(''); // PO#, Invoice#, etc.
  const [reference2, setReference2] = useState<string>(''); // Additional reference
  const [packagePresets, setPackagePresets] = useState<Array<{
    id: string;
    name: string;
    length: number;
    width: number;
    height: number;
    weight?: number;
    is_default?: boolean;
  }>>([]);
  const [showNewPresetForm, setShowNewPresetForm] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');

  // Refresh order data from API
  const refreshOrderData = async (reinitializeBoxes = false) => {
    if (!order?.id) return;

    try {
      const result = await ordersApi.get(order.id);
      // Merge order data with items
      const freshOrder = {
        ...result.order,
        items: result.items, // Items come separately in the response
      };
      setRefreshedOrder(freshOrder);
      
      // Reinitialize boxes with fresh items if requested (e.g., coming from shipping station)
      if (reinitializeBoxes && result.items) {
        const freshUnfulfilledItems = result.items.filter((item: any) => {
          const unfulfilledQty = item.unfulfilled_quantity !== undefined
            ? parseInt(item.unfulfilled_quantity)
            : item.quantity;
          return unfulfilledQty > 0;
        }).map((item: any) => ({
          ...item,
          quantity: item.unfulfilled_quantity !== undefined ? parseInt(item.unfulfilled_quantity) : item.quantity,
        }));
        
        console.log('[OrderDetailsModal] Reinitializing boxes with fresh items:', freshUnfulfilledItems.map((i: any) => ({
          title: i.title?.substring(0, 30),
          variant_title: i.variant_title,
          qty: i.quantity,
          weight: i.variant_weight,
        })));
        
        setBoxes([{
          id: '1',
          items: freshUnfulfilledItems.map((item: any) => ({ ...item, selected: true })),
          weight: freshUnfulfilledItems.reduce((sum: number, item: any) => 
            sum + (parseFloat(item.variant_weight || item.product_weight || 0) * item.quantity), 0),
          length: 12,
          width: 12,
          height: 6,
        }]);
      }
    } catch (error: any) {
      console.error('Failed to refresh order:', error);
    }
  };

  // Fetch fresh order data on mount when opening from shipping station
  // This ensures we get non-expanded bundle items
  React.useEffect(() => {
    if (initialTab === 'ship' && order?.id) {
      console.log('[OrderDetailsModal] Opening in ship tab - fetching fresh order data');
      refreshOrderData(true); // Pass true to reinitialize boxes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load shipments when tracking tab is opened
  const loadShipments = async () => {
    if (!order?.id) return;

    setLoadingShipments(true);
    try {
      const result = await shippingApi.getShipments(order.id);
      const shipmentsList = result.shipments || [];
      setShipments(shipmentsList);
      
      // Also load claims for these shipments
      if (shipmentsList.length > 0) {
        loadClaimsForShipments(shipmentsList);
      }
    } catch (error: any) {
      console.error('Failed to load shipments:', error);
    } finally {
      setLoadingShipments(false);
    }
  };

  // Load shipments when switching to shipments tab
  React.useEffect(() => {
    if (activeTab === 'shipments' && order?.id) {
      loadShipments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, order?.id]);

  const handleManualShipment = async () => {
    if (!order) return;
    if (!manualCarrier || !manualTracking.trim()) {
      toast.error('Missing Info', 'Carrier and tracking are required for manual shipments');
      return;
    }

    setManualSubmitting(true);
    try {
      const itemsForPackage =
        !manualSkipFulfillment && manualIncludeItems && (unfulfilledItems.length > 0 ? unfulfilledItems : []);

      const packageItems = Array.isArray(itemsForPackage)
        ? itemsForPackage.map((item: any) => ({
            order_item_id: item.id,
            shopify_line_item_id: (item as any).shopify_line_item_id,
            quantity: item.quantity,
          }))
        : [];

      await shippingApi.createManualShipment(order.id, {
        carrier: manualCarrier,
        tracking_number: manualTracking.trim(),
        cost: manualCost ? parseFloat(manualCost) : 0,
        service_level: manualNotes ? 'Manual/Freight' : 'Manual',
        skip_fulfillment: manualSkipFulfillment,
        packages: [
          {
            tracking_number: manualTracking.trim(),
            items: packageItems,
          },
        ],
      });

      // Audit trail
      if (employee) {
        logActivity('manual_shipment_added', `Manual shipment ${manualTracking.trim()} via ${manualCarrier}`);
      }

      toast.success('Manual Shipment Added', 'Tracking recorded and fulfillment updated');
      setManualTracking('');
      setManualCost('');
      setManualNotes('');
      setManualIncludeItems(true);
      loadShipments();
      onShip();
      setActiveTab('shipments');
    } catch (error: any) {
      toast.error('Manual Shipment Failed', error.response?.data?.error || error.message);
    } finally {
      setManualSubmitting(false);
    }
  };

  // Load package presets on mount
  React.useEffect(() => {
    const loadPresets = async () => {
      try {
        const result = await shippingApi.getPackagePresets();
        setPackagePresets(result.presets || []);
      } catch (error) {
        console.error('Failed to load package presets:', error);
      }
    };
    loadPresets();
  }, []);

  // Apply automation settings from order (set at import time, like ShipStation)
  // IMPORTANT: Only apply if there's NO draft (first time opening) to avoid overwriting user's changes
  // Check if draft has any meaningful data (rates fetched, or explicit insurance settings saved)
  const hasExistingDraft = !!(draft?.availableRates?.length || draft?.step === 'rates' || draft?.step === 'labels' || draft?.insuranceOption !== undefined);
  React.useEffect(() => {
    // Skip if we have an existing draft - user's previous settings should be preserved
    if (hasExistingDraft) {
      console.log('[Automation] Skipping auto-apply - existing draft found');
      return;
    }

    const orderData = refreshedOrder || order;
    if (!orderData) return;

    // Check if order has automation-applied insurance
    // @ts-ignore - these fields may not be in the type yet
    if (orderData.auto_insurance && orderData.auto_insurance_amount) {
      // @ts-ignore
      const autoAmount = parseFloat(orderData.auto_insurance_amount);
      if (autoAmount > 0) {
        // Use the provider from automation rule, default to parcelguard (cheaper)
        // @ts-ignore
        const autoProvider = orderData.auto_insurance_provider || 'parcelguard';
        setInsuranceOption(autoProvider as 'parcelguard' | 'carrier');
        setInsuranceAmount(autoAmount);
        // @ts-ignore
        console.log(`[Automation] Applied auto-insurance: ${autoProvider} $${autoAmount} from rule "${orderData.automation_rule_name || 'Unknown'}"`);
      }
    }

    // Check if order has automation-applied signature requirement
    // @ts-ignore
    if (orderData.auto_signature_required) {
      setSignatureRequired('signature');
      console.log('[Automation] Applied signature requirement from automation rule');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, refreshedOrder?.id]);

  // NOTE: Auto-refresh for insurance changes has been disabled
  // Users should manually click "Get Rates" when changing insurance settings
  // This prevents unexpected rate refreshes that were occurring after 10-15 seconds
  // The old auto-refresh logic was causing issues when state values got out of sync

  // CRITICAL: Update boxes when items change (e.g., after data loads or refresh)
  // This fixes the bug where items show in Order Summary but not in Configure Shipment
  // IMPORTANT: Preserve existing dimensions when updating items!
  React.useEffect(() => {
    // Only update if:
    // 1. We have unfulfilled items
    // 2. Current boxes have no items OR no draft exists
    // 3. We're not in the middle of configuring shipment (draft exists with labels or rates)
    const boxesAreEmpty = boxes.every(box => box.items.length === 0);
    const hasDraftWithProgress = draft?.labels?.length > 0 || draft?.selectedRate || availableRates.length > 0;
    
    if (unfulfilledItems.length > 0 && boxesAreEmpty && !hasDraftWithProgress) {
      console.log('[OrderDetailsModal] Updating boxes with', unfulfilledItems.length, 'items');
      // PRESERVE existing box dimensions - don't reset to defaults!
      const existingBox = boxes[0] || {};
      setBoxes([{
        id: '1',
        items: unfulfilledItems.map((item: any) => ({ ...item, selected: true })),
        weight: unfulfilledItems.reduce((sum: number, item: any) => 
          sum + (parseFloat(item.variant_weight || item.product_weight || 0) * item.quantity), 0),
        // Preserve dimensions if they exist, otherwise use defaults
        length: existingBox.length || 12,
        width: existingBox.width || 12,
        height: existingBox.height || 6,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfulfilledItems.length, items.length]);

  // Load activity log
  const loadActivityLog = async () => {
    if (!order) return;
    setLoadingActivity(true);
    try {
      const data = await employeesApi.getOrderActivity(order.id);
      setActivityLog(data.activities || []);
    } catch (error) {
      console.error('Failed to load activity log:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Auto-load activity when order changes and activity panel is visible
  useEffect(() => {
    if (showActivityLog && order?.id) {
      loadActivityLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, showActivityLog]);

  // Ensure history tab shows fresh data
  useEffect(() => {
    if (activeTab === 'history') {
      setShowActivityLog(true);
      if (order?.id && activityLog.length === 0 && !loadingActivity) {
        loadActivityLog();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  if (!order) return null;

  // Format activity action for display
  const formatActionType = (actionType: string) => {
    const labels: Record<string, string> = {
      order_scanned: 'Order Scanned',
      item_scanned: 'Item Scanned',
      items_verified: 'Items Verified',
      item_packed: 'Item Packed',
      packing_complete: 'Packing Complete',
      order_packed: 'Order Packed',
      labels_created: 'Labels Created',
      shipment_confirmed: 'Shipment Confirmed',
      shipment_voided: 'Shipment Voided',
      labels_voided: 'Labels Voided',
      manual_shipment_added: 'Manual Shipment Added',
      address_updated: 'Address Updated',
    };
    return labels[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Handle void shipment
  const handleVoidShipment = async (shipmentId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to void this shipment?\n\n• Labels will be cancelled with carrier\n• Items will be unfulfilled\n• Order will be available for re-shipping\n\nFor USPS labels: Refunds take 2-4 weeks to be approved by USPS.')) {
      return;
    }

    try {
      const result = await shippingApi.voidShipment(shipmentId);

      let message = 'Items have been unfulfilled and can now be re-shipped.';

      // Add refund info if USPS
      if (result.pending_refund_amount) {
        message += `\n\nUSPS Refund: $${result.pending_refund_amount.toFixed(2)}\n${result.refund_note}`;
      }

      if (result.warning) {
        message += `\n\n${result.warning}`;
      }

      toast.success('Shipment Voided', message);

      // Log activity
      logActivity('shipment_voided', 'Voided shipment from tracking tab');

      // Clear localStorage draft and refresh order data
      removeShippingDraft(draftKey);
      await refreshOrderData();
      await loadShipments();
      setActiveTab('shipments');
    } catch (error: any) {
      toast.error('Void Failed', error.response?.data?.error || error.message);
    }
  };

  // Open the return label modal with items from the outbound shipment
  const handleOpenReturnModal = (shipment: any) => {
    if (!shipment.packages?.length) return;

    // Aggregate items across ALL packages in the shipment
    const aggregated: Record<string, { order_item_id: string; title: string; sku: string; totalQty: number }> = {};
    for (const pkg of shipment.packages) {
      const pkgItems = pkg.items
        ? (typeof pkg.items === 'string' ? JSON.parse(pkg.items) : pkg.items)
        : [];
      for (const pi of pkgItems) {
        const key = pi.order_item_id || pi.id;
        if (!key) continue;
        if (!aggregated[key]) {
          aggregated[key] = { order_item_id: key, title: pi.title || 'Unknown Item', sku: pi.sku || '', totalQty: 0 };
        }
        aggregated[key].totalQty += parseInt(pi.quantity) || 1;
      }
    }

    // Tally already-returned quantities from existing non-voided return shipments
    const alreadyReturned: Record<string, number> = {};
    for (const s of shipments) {
      if (s.is_return && s.return_for_shipment_id === shipment.id && s.status !== 'voided') {
        for (const pkg of (s.packages || [])) {
          const pItems = pkg.items ? (typeof pkg.items === 'string' ? JSON.parse(pkg.items) : pkg.items) : [];
          for (const pi of pItems) {
            const key = pi.order_item_id || pi.id;
            if (key) alreadyReturned[key] = (alreadyReturned[key] || 0) + (parseInt(pi.quantity) || 1);
          }
        }
      }
    }

    // Cross-reference with order items for weights
    const orderItems = currentOrder?.items || [];
    const items = Object.values(aggregated)
      .map((agg) => {
        const orderItem = orderItems.find((oi: any) => oi.id === agg.order_item_id);
        const unitWeight = orderItem
          ? (parseFloat(orderItem.variant_weight) || parseFloat(orderItem.product_weight) || parseFloat(orderItem.weight_oz) || 0)
          : 0;
        const remaining = Math.max(0, agg.totalQty - (alreadyReturned[agg.order_item_id] || 0));
        return {
          order_item_id: agg.order_item_id,
          title: orderItem?.title || agg.title,
          sku: orderItem?.sku || agg.sku,
          quantity: Math.min(1, remaining),
          maxQty: remaining,
          unitWeight,
          selected: remaining > 0,
        };
      })
      .filter((item) => item.maxQty > 0);

    if (items.length === 0) {
      toast.info('All Returned', 'All items from this shipment have already been returned.');
      return;
    }

    setReturnItems(items);
    const firstPkg = shipment.packages[0];
    setReturnDims({
      length: parseFloat(firstPkg.length) || 12,
      width: parseFloat(firstPkg.width) || 10,
      height: parseFloat(firstPkg.height) || 6,
    });
    setReturnWeightLb(null);
    setReturnWeightOz(null);
    setReturnModalShipment(shipment);
  };

  const returnCalcWeight = returnItems
    .filter(i => i.selected)
    .reduce((sum, i) => sum + i.unitWeight * i.quantity, 0);
  const returnHasOverride = returnWeightLb !== null || returnWeightOz !== null;
  const returnTotalWeight = returnHasOverride
    ? ((returnWeightLb || 0) * 16) + (returnWeightOz || 0)
    : returnCalcWeight;
  const returnDisplayLb = returnHasOverride ? (returnWeightLb || 0) : Math.floor(returnCalcWeight / 16);
  const returnDisplayOz = returnHasOverride ? (returnWeightOz || 0) : Math.round(returnCalcWeight % 16);
  const returnOverMaxWeight = returnTotalWeight > maxPackageWeightOz;

  // Submit the return label
  const handleSubmitReturnLabel = async () => {
    if (!returnModalShipment) return;
    const selectedItems = returnItems.filter(i => i.selected && i.quantity > 0);
    if (selectedItems.length === 0) {
      toast.error('No Items Selected', 'Select at least one item to return.');
      return;
    }

    setCreatingReturnLabel(returnModalShipment.id);
    try {
      const result = await shippingApi.createReturnLabel(returnModalShipment.id, {
        items: selectedItems.map(i => ({ order_item_id: i.order_item_id, quantity: i.quantity })),
        weight: returnTotalWeight,
        length: returnDims.length,
        width: returnDims.width,
        height: returnDims.height,
      });

      toast.success('Return Label Created', `Tracking: ${result.tracking_number} — Cost: $${parseFloat(result.cost).toFixed(2)}`);

      if (result.label_data) {
        downloadLabel(result.label_data, `return-label-${result.tracking_number}`);
      }

      logActivity('return_label_created', `Created return label ${result.tracking_number}`);
      setReturnModalShipment(null);
      await loadShipments();
    } catch (error: any) {
      toast.error('Return Label Failed', error.response?.data?.error || error.message);
    } finally {
      setCreatingReturnLabel(null);
    }
  };

  // Load claims for shipments
  const loadClaimsForShipments = async (shipmentsList: any[]) => {
    const claims: Record<string, any> = {};
    for (const shipment of shipmentsList) {
      try {
        const result = await claimsApi.getByShipment(shipment.id);
        if (result.claim) {
          claims[shipment.id] = result.claim;
        }
      } catch (error) {
        // Claim might not exist, that's okay
      }
    }
    setShipmentClaims(claims);
  };

  // Open claim modal
  const handleOpenClaimModal = (shipment: any) => {
    setClaimShipment(shipment);
    setClaimType('lost');
    setClaimDescription('');
    setClaimAmount(currentOrder?.total_price?.toString() || '');
    setShowClaimModal(true);
  };

  // Submit claim
  const handleSubmitClaim = async () => {
    if (!claimShipment || !currentOrder) return;

    setSubmittingClaim(true);
    try {
      const result = await claimsApi.create({
        shipment_id: claimShipment.id,
        order_id: currentOrder.id,
        claim_type: claimType,
        description: claimDescription || undefined,
        claim_amount: claimAmount ? parseFloat(claimAmount) : undefined,
      });

      toast.success('Claim Created', 'Issue reported successfully. Track status in the shipment details.');
      setShowClaimModal(false);
      
      // Update local claims state
      setShipmentClaims(prev => ({
        ...prev,
        [claimShipment.id]: result.claim,
      }));

      // If there's a carrier claim URL, offer to open it
      if (result.carrier_claim_url) {
        // eslint-disable-next-line no-restricted-globals
        if (confirm(`Would you like to file a claim directly with ${claimShipment.carrier}?\n\nThis will open the carrier's claim portal in a new tab.`)) {
          window.open(result.carrier_claim_url, '_blank');
        }
      }
    } catch (error: any) {
      toast.error('Failed to Create Claim', error.response?.data?.error || error.message);
    } finally {
      setSubmittingClaim(false);
    }
  };

  // Save draft
  const saveDraft = () => {
    const draftData = {
      step,
      boxes,
      availableRates,
      selectedRate,
      labels,
      // Save insurance settings so they're restored when reopening
      insuranceOption,
      insuranceAmount,
      ratesInsuranceAmount,
      ratesInsuranceProvider,
    };
    saveShippingDraft(draftKey, draftData);
  };

  const handleQuickBlock = async () => {
    if (!currentOrder) return;
    setBlockSaving(true);
    try {
      const fullName =
        (currentOrder as any).customer_name ||
        [currentOrder.first_name, currentOrder.last_name].filter(Boolean).join(' ');
      await blockedAddressesApi.create({
        label: `${fullName || currentOrder.shipping_city || 'Order'} - blocked from #${currentOrder.order_number}`,
        name: fullName || undefined,
        email: currentOrder.customer_email || undefined,
        address_line1: currentOrder.shipping_address_line1 || undefined,
        address_line2: currentOrder.shipping_address_line2 || undefined,
        city: currentOrder.shipping_city || undefined,
        state: currentOrder.shipping_state || undefined,
        zip: currentOrder.shipping_zip || undefined,
        country: currentOrder.shipping_country || 'US',
        reason: blockReason || `Blocked from order #${currentOrder.order_number}`,
      });
      toast.success('Address Blocked', 'Future matching orders will be auto-flagged.');
      setShowBlockForm(false);
      setBlockReason('');
      refreshOrderData();
    } catch (e: any) {
      toast.error('Failed', e.response?.data?.error || e.message || 'Could not add to blocklist');
    } finally {
      setBlockSaving(false);
    }
  };

  const handleUnblockOrder = async () => {
    if (!currentOrder) return;
    if (!window.confirm('Clear the block flag on this order? You can still keep the address on the blocklist.')) return;
    try {
      await blockedAddressesApi.unblockOrder(currentOrder.id);
      toast.success('Order Unblocked', 'Order is cleared for fulfillment.');
      refreshOrderData();
    } catch (e: any) {
      toast.error('Failed', e.response?.data?.error || e.message);
    }
  };

  const handleClose = () => {
    if (hasUnconfirmedLabels) {
      const confirmed = window.confirm(
        'You have unconfirmed shipping labels. If you close without confirming, tracking will NOT be sent to Shopify.\n\nAre you sure you want to close?'
      );
      if (!confirmed) return;
    }
    saveDraft();
    onClose();
  };

  const handleSplitBoxes = () => {
    console.log('[BoxOps] handleSplitBoxes called');
    console.log('[BoxOps] Current boxes before split:', JSON.stringify(boxes.map(b => ({ id: b.id, items: b.items.map(i => ({ id: i.id, title: i.title?.substring(0, 30), qty: i.quantity })), weight: b.weight }))));
    setBoxes([
      {
        id: '1',
        items: [],
        weight: 0,
        length: 12,
        width: 12,
        height: 6,
      },
      {
        id: '2',
        items: [],
        weight: 0,
        length: 12,
        width: 12,
        height: 6,
      },
    ]);
    console.log('[BoxOps] Split into 2 empty boxes');
  };

  const toggleItemInBox = (boxId: string, item: any) => {
    console.log('[BoxOps] toggleItemInBox called', { boxId, itemId: item.id, itemTitle: item.title?.substring(0, 30), itemQty: item.quantity });
    setBoxes(prevBoxes => {
      console.log('[BoxOps] toggleItemInBox - prevBoxes:', JSON.stringify(prevBoxes.map(b => ({ id: b.id, items: b.items.map(i => ({ id: i.id, qty: i.quantity })) }))));
      const targetBox = prevBoxes.find(b => b.id === boxId);
      const itemExistsInTarget = targetBox?.items.find(i => i.id === item.id);
      console.log('[BoxOps] toggleItemInBox - itemExistsInTarget:', !!itemExistsInTarget);

      const updatedBoxes = prevBoxes.map(box => {
        const filteredItems = box.items.filter(i => i.id !== item.id);

        if (box.id === boxId) {
          // IMPORTANT: Create a copy of the item to avoid shared references between boxes
          const newItems = itemExistsInTarget ? filteredItems : [...filteredItems, { ...item }];
          console.log('[BoxOps] toggleItemInBox - box', box.id, 'newItems:', newItems.map(i => ({ id: i.id, qty: i.quantity })));

          const weight = newItems.reduce((sum, i) => {
            const itemWeight = parseFloat(i.variant_weight || i.product_weight || 0);
            return sum + (itemWeight * i.quantity);
          }, 0);

          return { ...box, items: newItems, weight };
        }

        const weight = filteredItems.reduce((sum, i) => {
          const itemWeight = parseFloat(i.variant_weight || i.product_weight || 0);
          return sum + (itemWeight * i.quantity);
        }, 0);

        return { ...box, items: filteredItems, weight };
      });

      console.log('[BoxOps] toggleItemInBox - result:', JSON.stringify(updatedBoxes.map(b => ({ id: b.id, items: b.items.map(i => ({ id: i.id, qty: i.quantity })), weight: b.weight }))));
      return updatedBoxes;
    });
  };

  const handleRemoveBox = (boxId: string) => {
    console.log('[BoxOps] handleRemoveBox called', { boxId });
    setBoxes(prevBoxes => {
      console.log('[BoxOps] handleRemoveBox - prevBoxes:', JSON.stringify(prevBoxes.map(b => ({ id: b.id, itemCount: b.items.length }))));
      const remainingBoxes = prevBoxes.filter(b => b.id !== boxId);

      if (remainingBoxes.length === 1) {
        const allItems = unfulfilledItems.map((item: any) => ({ ...item, selected: true }));
        const totalWeight = allItems.reduce((sum: number, item: any) => {
          const itemWeight = parseFloat((item as any).variant_weight || (item as any).product_weight || 0);
          return sum + (itemWeight * item.quantity);
        }, 0);

        return [{
          ...remainingBoxes[0],
          items: allItems,
          weight: totalWeight,
        }];
      }

      return remainingBoxes;
    });
  };

  // Re-sync order from Shopify to get latest shipping_lines
  const handleResyncOrder = async () => {
    if (!order?.id) return;
    
    setIsResyncing(true);
    try {
      const result = await ordersApi.resync(order.id);
      
      // Update local order state with refreshed data
      if (result.order) {
        setRefreshedOrder((prev: any) => ({
          ...prev,
          ...order,
          ...result.order,
        }));
      }
      
      toast.success('Order Resynced', `Shipping method: ${result.shipping_method || 'None'}`);
    } catch (error: any) {
      toast.error('Resync Failed', error.response?.data?.message || error.message);
    } finally {
      setIsResyncing(false);
    }
  };

  const handleGetRates = async () => {
    if (!order) return;

    // Check if shipping address exists
    if (!currentOrder.shipping_address_line1) {
      toast.warning('Missing Address', 'No shipping address on file for this order');
      return;
    }

    setIsFetchingRates(true);
    try {
      // Pass insurance amount and provider to get accurate rates
      const currentInsurance = insuranceOption !== 'none' && insuranceAmount > 0 ? insuranceAmount : 0;
      const currentProvider = currentInsurance > 0 ? insuranceOption : 'none';
      const result = await shippingApi.getRates(order.id, boxes, currentInsurance > 0 ? currentInsurance : undefined, currentProvider);
      setAvailableRates(result.rates);
      setRatesInsuranceAmount(currentInsurance); // Track insurance used for these rates
      setRatesInsuranceProvider(currentProvider); // Track provider used
      
      // Store method match info for display
      if (result.method_match) {
        setMethodMatch(result.method_match);
      }

      // Auto-select rate based on Shopify shipping method
      let autoSelectedRate = null;
      
      if (result.method_match?.requires_free_shipping) {
        // Free shipping - select cheapest rate
        const sortedRates = [...result.rates].sort((a: any, b: any) => a.total_cost - b.total_cost);
        if (sortedRates.length > 0) {
          autoSelectedRate = sortedRates[0];
        }
      } else if (result.method_match?.suggested_rate_index !== null && result.method_match?.suggested_rate_index !== undefined) {
        // Matched rate - select the suggested one
        autoSelectedRate = result.rates[result.method_match.suggested_rate_index];
      }
      
      setSelectedRate(autoSelectedRate);

      // Mark address as verified if USPS validation passed
      if (result.address_validation?.valid) {
        setAddressVerified(true);
      }

      setStep('rates');
    } catch (error: any) {
      toast.error('Failed to Get Rates', error.response?.data?.error || error.message);
    } finally {
      setIsFetchingRates(false);
    }
  };

  const handleCreateLabels = async () => {
    if (!order || !selectedRate) {
      toast.warning('No Rate Selected', 'Please select a shipping service');
      return;
    }

    if (creatingLabels) {
      return; // Prevent double-click
    }

    setCreatingLabels(true);
    try {
      const result = await shippingApi.createLabels(order.id, boxes, selectedRate, {
        labelFormat,
        insuranceAmount: insuranceAmount > 0 ? insuranceAmount : undefined,
        insuranceProvider: insuranceOption !== 'none' && insuranceAmount > 0 ? insuranceOption : undefined,
        signatureRequired,
        reference1: reference1 || undefined,
        reference2: reference2 || undefined,
        employeeId: employee?.id,
      });

      // Format labels for display - KEEP label_data for downloading!
      const createdLabels = result.packages.map((pkg: any, index: number) => ({
        tracking: pkg.tracking_number,
        carrier: selectedRate.carrier.toUpperCase(),
        service: selectedRate.service_name,
        cost: pkg.cost || selectedRate.boxes?.[index] || selectedRate.total_cost / boxes.length,
        status: 'pending',
        box_number: index + 1,
        label_data: pkg.label_data, // IMPORTANT: Keep label data for printing/downloading
        label_format: pkg.label_format || labelFormat, // Store the format used
        customs_form_url: pkg.customs_form_url, // URL for international customs forms
        is_international: pkg.is_international,
        shipment_id: result.shipment_id, // Store shipment ID for confirmation
      }));

      setLabels(createdLabels);
      setStep('labels');
    } catch (error: any) {
      // Handle insufficient balance for USPS
      if (error.response?.status === 402) {
        const data = error.response.data;
        toast.error('Insufficient Wallet Balance', 
          `Current: $${data.balance?.toFixed(2) || '0.00'}\nRequired: $${data.required?.toFixed(2)}\n\nGo to Settings → Wallet to add funds`
        );
      } else {
        toast.error('Failed to Create Labels', error.response?.data?.error || error.message);
      }
    } finally {
      setCreatingLabels(false);
    }
  };

  const handleConfirmShipment = async () => {
    if (labels.length === 0 || !labels[0].shipment_id) {
      toast.warning('No Labels', 'No labels to confirm');
      return;
    }

    try {
      await shippingApi.confirmShipment(labels[0].shipment_id, false, employee?.id);

      // Update labels status to confirmed
      setLabels(labels.map(label => ({ ...label, status: 'confirmed' })));

      // Reload shipments for tracking tab
      await loadShipments();

      removeShippingDraft(draftKey);
      toast.success('Shipment Confirmed', 'Tracking sent to Shopify. View details in the Tracking tab.');
      onShip(); // Refresh orders list

      // Don't close modal - user can still print labels or switch to tracking tab!
      // User can manually close when ready
    } catch (error: any) {
      console.error('Confirm shipment error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      toast.error('Confirmation Failed', errorMessage);
    }
  };

  const handleVoidLabels = async () => {
    if (labels.length === 0 || !labels[0].shipment_id) {
      // No shipment created yet, just clear local state
      setLabels([]);
      setStep('boxes');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm('This will void the labels with the carrier and reverse the fulfillment.\n\n• Labels will be cancelled (no charges for UPS/USPS)\n• Items will be unfulfilled\n• You can create new labels\n\nContinue?')) {
      return;
    }

    try {
      await shippingApi.voidShipment(labels[0].shipment_id);

      // Log activity
      logActivity('labels_voided', `Voided ${labels.length} label(s) before confirmation`);

      // Clear local state and localStorage draft
      setLabels([]);
      setStep('boxes');
      removeShippingDraft(draftKey);

      toast.success('Labels Voided', 'Refreshing order data...');

      // Refresh order data to show updated fulfillment quantities
      await refreshOrderData();
    } catch (error: any) {
      console.error('Void labels error:', error);
      toast.error('Void Failed', error.response?.data?.error || error.message);
    }
  };

  const handleSplitQuantity = (item: any, splits: { boxId: string; quantity: number }[], newBoxQuantity?: number) => {
    console.log('[BoxOps] handleSplitQuantity called', { itemId: item.id, itemTitle: item.title?.substring(0, 30), itemQty: item.quantity, splits, newBoxQuantity });
    const totalSplitToExisting = splits.reduce((sum, s) => sum + s.quantity, 0);
    const totalSplit = totalSplitToExisting + (newBoxQuantity || 0);
    const remaining = item.quantity - totalSplit;
    console.log('[BoxOps] handleSplitQuantity - totalSplitToExisting:', totalSplitToExisting, 'newBoxQuantity:', newBoxQuantity, 'remaining:', remaining);

    // Find which box originally had this item
    const originalBoxId = boxes.find(box => box.items.some(i => i.id === item.id))?.id;
    console.log('[BoxOps] handleSplitQuantity - originalBoxId:', originalBoxId);

    setBoxes(prevBoxes => {
      // First, update existing boxes
      let updatedBoxes = prevBoxes.map(box => {
        // Filter out the item from this box
        const filteredItems = box.items.filter(i => i.id !== item.id);

        // Find if this box should get a split
        const split = splits.find(s => s.boxId === box.id);

        let newItems = filteredItems;

        if (split && split.quantity > 0) {
          // Add item with split quantity
          const splitItem = { ...item, quantity: split.quantity };
          newItems = [...filteredItems, splitItem];
        }

        // If there are remaining units and this is the original box, keep them here
        if (remaining > 0 && box.id === originalBoxId && !split) {
          const remainingItem = { ...item, quantity: remaining };
          newItems = [...filteredItems, remainingItem];
        }

        // Recalculate weight
        const weight = newItems.reduce((sum, i) => {
          const itemWeight = parseFloat(i.variant_weight || i.product_weight || 0);
          return sum + (itemWeight * i.quantity);
        }, 0);

        return { ...box, items: newItems, weight };
      });

      // Then, create new box if requested
      if (newBoxQuantity && newBoxQuantity > 0) {
        const newBoxId = String(updatedBoxes.length + 1);
        const itemWeight = parseFloat(item.variant_weight || item.product_weight || 0);
        console.log('[BoxOps] handleSplitQuantity - creating new box', newBoxId, 'with qty:', newBoxQuantity);
        const newBox = {
          id: newBoxId,
          items: [{ ...item, quantity: newBoxQuantity }],
          weight: itemWeight * newBoxQuantity,
          length: 12,
          width: 12,
          height: 6,
        };
        updatedBoxes = [...updatedBoxes, newBox];
      }

      console.log('[BoxOps] handleSplitQuantity - result:', JSON.stringify(updatedBoxes.map(b => ({ id: b.id, items: b.items.map(i => ({ id: i.id, qty: i.quantity })), weight: b.weight }))));
      return updatedBoxes;
    });

    setSplittingItem(null);
  };

  const handleSplitToNewBox = (item: any, quantity: number) => {
    console.log('[BoxOps] handleSplitToNewBox called', { itemId: item.id, itemTitle: item.title?.substring(0, 30), quantity, currentBoxCount: boxes.length });
    // Create a new box with this item
    const newBoxId = String(boxes.length + 1);
    console.log('[BoxOps] handleSplitToNewBox - creating new box with id:', newBoxId);
    const newBox = {
      id: newBoxId,
      items: [{ ...item, quantity }],
      weight: parseFloat(item.variant_weight || item.product_weight || 0) * quantity,
      length: 12,
      width: 12,
      height: 6,
    };

    // Update original item quantity in existing boxes
    setBoxes(prevBoxes => {
      const updated = prevBoxes.map(box => {
        const itemInBox = box.items.find(i => i.id === item.id);
        if (itemInBox) {
          const newQuantity = itemInBox.quantity - quantity;
          if (newQuantity <= 0) {
            // Remove item if no units left
            const filteredItems = box.items.filter(i => i.id !== item.id);
            const weight = filteredItems.reduce((sum, i) => {
              const itemWeight = parseFloat(i.variant_weight || i.product_weight || 0);
              return sum + (itemWeight * i.quantity);
            }, 0);
            return { ...box, items: filteredItems, weight };
          } else {
            // Update quantity
            const updatedItems = box.items.map(i =>
              i.id === item.id ? { ...i, quantity: newQuantity } : i
            );
            const weight = updatedItems.reduce((sum, i) => {
              const itemWeight = parseFloat(i.variant_weight || i.product_weight || 0);
              return sum + (itemWeight * i.quantity);
            }, 0);
            return { ...box, items: updatedItems, weight };
          }
        }
        return box;
      });

      return [...updated, newBox];
    });

    setSplittingItem(null);
  };

  // Apply a package preset to a box
  const applyPresetToBox = (boxIndex: number, preset: typeof packagePresets[0]) => {
    setBoxes(prevBoxes => {
      const updated = [...prevBoxes];
      updated[boxIndex] = {
        ...updated[boxIndex],
        length: preset.length,
        width: preset.width,
        height: preset.height,
      };
      return updated;
    });
  };

  // Save current box dimensions as a new preset
  const saveAsPreset = async (boxIndex: number) => {
    const box = boxes[boxIndex];
    if (!newPresetName.trim()) {
      toast.warning('Missing Name', 'Please enter a preset name');
      return;
    }

    try {
      const result = await shippingApi.addPackagePreset({
        name: newPresetName.trim(),
        length: box.length,
        width: box.width,
        height: box.height,
      });
      setPackagePresets(result.presets);
      setShowNewPresetForm(false);
      setNewPresetName('');
      toast.success('Preset Saved', `"${newPresetName}" saved successfully`);
    } catch (error: any) {
      toast.error('Save Failed', error.message);
    }
  };

  const handleQuickSplit = (item: any, qtyPerBox: number, boxDimensions: { length: number; width: number; height: number }) => {
    console.log('[BoxOps] handleQuickSplit called', { itemId: item.id, itemTitle: item.title?.substring(0, 30), itemQty: item.quantity, qtyPerBox, boxDimensions });
    const numBoxes = Math.ceil(item.quantity / qtyPerBox);
    console.log('[BoxOps] handleQuickSplit - will create', numBoxes, 'new boxes');
    const newBoxes: Box[] = [];

    // Create new boxes
    for (let i = 0; i < numBoxes; i++) {
      const qty = Math.min(qtyPerBox, item.quantity - (i * qtyPerBox));
      const itemWeight = parseFloat(item.variant_weight || item.product_weight || 0);
      const newBoxId = String(boxes.length + i + 1);
      console.log('[BoxOps] handleQuickSplit - creating box', newBoxId, 'with qty:', qty);

      const newBox: Box = {
        id: newBoxId,
        items: [{ ...item, quantity: qty }],
        weight: itemWeight * qty,
        length: boxDimensions.length,
        width: boxDimensions.width,
        height: boxDimensions.height,
      };

      newBoxes.push(newBox);
    }

    // Remove item from current boxes and add new boxes
    setBoxes(prevBoxes => {
      const clearedBoxes = prevBoxes.map(box => {
        const filteredItems = box.items.filter(i => i.id !== item.id);
        const weight = filteredItems.reduce((sum, i) => {
          const itemWeight = parseFloat(i.variant_weight || i.product_weight || 0);
          return sum + (itemWeight * i.quantity);
        }, 0);
        return { ...box, items: filteredItems, weight };
      });

      return [...clearedBoxes, ...newBoxes];
    });

    setSplittingItem(null);
  };

  const handlePrintOrder = () => {
    const orderNum = /^[0-9]+$/.test(currentOrder.order_number)
      ? `#PSCS${currentOrder.order_number}`
      : currentOrder.order_number;
    const orderNumClean = orderNum.replace('#', '');
    const orderDate = currentOrder.shopify_created_at
      ? new Date(currentOrder.shopify_created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' })
      : new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });
    const customerName = `${currentOrder.first_name || ''} ${currentOrder.last_name || ''}`.trim() || 'N/A';
    const customerEmail = currentOrder.customer_email || '';
    const customerPhone = (currentOrder as any).customer_phone || '';
    const orderNotes = (currentOrder as any).notes || '';

    // Shipping method from shipping_lines
    const shippingLines: any[] = (currentOrder as any).shipping_lines || [];
    const shippingMethod = shippingLines.length > 0 ? shippingLines[0].title : '';
    const shippingCost = shippingLines.reduce((sum: number, line: any) => sum + parseFloat(line.price || '0'), 0);

    // Calculate subtotal from items
    const subtotal = resolvedItems.reduce((sum: number, item: any) => {
      return sum + (parseFloat(item.price || '0') * (item.quantity || 1));
    }, 0);
    const totalPrice = parseFloat(currentOrder.total_price || '0');
    const totalItems = resolvedItems.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);

    // Derive tax & discount (total = subtotal + shipping + tax - discount)
    const taxAndDiscount = totalPrice - subtotal - shippingCost;
    const totalTax = taxAndDiscount > 0 ? taxAndDiscount : 0;
    const discount = taxAndDiscount < 0 ? Math.abs(taxAndDiscount) : 0;

    // Format currency
    const fmt = (n: number) => `USD ${n.toFixed(2)}`;

    // Number to words
    const numberToWords = (n: number): string => {
      const ones = ['','one','two','three','four','five','six','seven','eight','nine',
        'ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
      const tens = ['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];
      const dollars = Math.floor(n);
      const cents = Math.round((n - dollars) * 100);
      const centsStr = cents > 0 ? `${cents < 10 ? 'zero ' : ''}${cents < 20 ? ones[cents] : tens[Math.floor(cents / 10)] + (cents % 10 ? '-' + ones[cents % 10] : '')} cents` : 'zero cents';
      if (dollars === 0) return `zero dollars ${centsStr}`;
      const convert = (num: number): string => {
        if (num < 20) return ones[num];
        if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
        if (num < 1000) return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + convert(num % 100) : '');
        if (num < 1000000) return convert(Math.floor(num / 1000)) + ' thousand' + (num % 1000 ? ' ' + convert(num % 1000) : '');
        return num.toString();
      };
      return `${convert(dollars)} dollars ${centsStr}`;
    };

    // Build address blocks
    const shipAddr = [
      currentOrder.shipping_address_line1,
      currentOrder.shipping_address_line2,
      `${currentOrder.shipping_city || ''}, ${currentOrder.shipping_state || ''}, ${currentOrder.shipping_zip || ''}`,
      currentOrder.shipping_country || 'United States',
    ].filter(Boolean);

    // Build items rows — include barcode canvas for each item
    let barcodeIdx = 0;
    const itemRows = resolvedItems.map((item: any) => {
      const itemTitle = item.title || item.product_title || 'Unknown';
      const sku = item.variant_sku || item.sku || item.product_sku || '';
      const barcode = item.variant_barcode || '';
      const price = parseFloat(item.price || '0');
      const qty = item.quantity || 1;
      const itemTotal = price * qty;
      const bcId = `item-bc-${barcodeIdx++}`;
      return `
        <tr>
          <td class="item-cell">
            <div class="item-name">${itemTitle}</div>
            ${sku ? `<div class="item-sku">Sku: ${sku}</div>` : ''}
            ${barcode ? `<div class="item-barcode"><svg id="${bcId}" data-barcode="${barcode}"></svg></div>` : ''}
          </td>
          <td class="item-cell" style="text-align:center;vertical-align:middle;">× ${qty}</td>
          <td class="item-cell" style="text-align:right;vertical-align:middle;">${fmt(price)}</td>
          <td class="item-cell" style="text-align:right;vertical-align:middle;">${fmt(itemTotal)}</td>
        </tr>`;
    }).join('');

    const html = `
      <html>
        <head>
          <title>Invoice ${orderNum}</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
          <style>
            @media print {
              body { margin: 0; }
              @page { margin: 0.4in 0.5in; }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, sans-serif;
              color: #1a1a2e;
              font-size: 13px;
              line-height: 1.45;
              max-width: 800px;
              margin: 0 auto;
              padding: 32px;
            }

            /* ── Header ── */
            .invoice-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 24px;
              padding-bottom: 18px;
              border-bottom: 3px solid #1a1a2e;
            }
            .company-block {
              flex: 1;
              padding-top: 0;
            }
            .logo-placeholder {
              width: 200px;
              height: 50px;
              margin-bottom: 8px;
              /* Logo will go here */
            }
            .company-name {
              font-size: 18px;
              font-weight: 700;
              margin-bottom: 4px;
            }
            .company-details {
              font-size: 11.5px;
              color: #444;
              line-height: 1.6;
            }
            .invoice-meta {
              text-align: right;
              min-width: 260px;
              padding-top: 0;
            }
            .invoice-number {
              font-size: 24px;
              font-weight: 800;
              color: #1a1a2e;
              margin-bottom: 4px;
            }
            .order-barcode {
              text-align: right;
              margin-bottom: 10px;
            }
            .order-barcode svg { display: inline-block; }
            .meta-grid {
              display: grid;
              grid-template-columns: auto auto;
              gap: 4px 16px;
              font-size: 11.5px;
              justify-content: end;
            }
            .meta-label {
              font-weight: 700;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.4px;
            }
            .meta-value {
              font-weight: 600;
              text-align: right;
            }

            /* ── Bill/Ship To ── */
            .address-row {
              display: flex;
              gap: 40px;
              margin-bottom: 24px;
            }
            .address-block { flex: 1; }
            .address-label {
              font-size: 10px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.8px;
              color: #999;
              margin-bottom: 6px;
              padding-bottom: 4px;
              border-bottom: 1px solid #e0e0e0;
            }
            .address-name { font-weight: 600; font-size: 14px; margin-bottom: 2px; }
            .address-line { font-size: 12px; color: #444; }
            .address-contact { font-size: 11px; color: #666; margin-top: 4px; }

            /* ── Items Table ── */
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .items-table th {
              text-align: left;
              padding: 10px 12px;
              background: #f5f5f8;
              font-size: 10px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: #666;
              border-bottom: 2px solid #ddd;
            }
            .items-table th:last-child,
            .items-table th:nth-child(3) { text-align: right; }
            .items-table th:nth-child(2) { text-align: center; }
            .item-cell { padding: 10px 12px; border-bottom: 1px solid #eee; vertical-align: top; }
            .item-name { font-weight: 500; font-size: 13px; }
            .item-sku { font-size: 10px; color: #888; margin-top: 2px; }
            .item-barcode { margin-top: 4px; }
            .item-barcode svg { display: block; }

            /* ── Totals ── */
            .totals-section { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
            .totals-left { font-size: 12px; color: #666; padding-top: 4px; }
            .totals-table { min-width: 240px; }
            .totals-table .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
            .totals-table .row.total {
              border-top: 2px solid #1a1a2e;
              margin-top: 6px;
              padding-top: 8px;
              font-size: 15px;
              font-weight: 700;
            }
            .totals-table .row .label { color: #666; }
            .totals-table .row.total .label { color: #1a1a2e; }
            .total-words { font-size: 11px; font-style: italic; color: #888; text-align: right; margin-top: 2px; margin-bottom: 16px; }

            /* ── Notes ── */
            .notes-section {
              background: #fffdf0; border: 1px solid #f0e6b8; border-radius: 6px;
              padding: 10px 14px; margin-bottom: 20px;
            }
            .notes-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #b08d28; margin-bottom: 4px; }
            .notes-text { font-size: 12px; color: #7a6520; white-space: pre-wrap; }

            /* ── Footer ── */
            .invoice-footer { text-align: center; padding-top: 20px; border-top: 1px solid #e0e0e0; margin-top: 20px; }
            .footer-thanks { font-size: 15px; font-weight: 600; margin-bottom: 4px; }
            .footer-note { font-size: 11px; color: #888; margin-bottom: 8px; }
            .footer-company { font-size: 11px; color: #aaa; }
          </style>
        </head>
        <body>

          <!-- Header -->
          <div class="invoice-header">
            <div class="company-block">
              <div class="logo-placeholder">
                <!-- Logo will go here -->
              </div>
              <div class="company-name">Peach State Candle Supply</div>
              <div class="company-details">
                105 Charles Court,<br>
                McDonough, Georgia, 30253<br>
                United States<br>
                cs@peachstatecandlesupply.com<br>
                4705070275
              </div>
            </div>
            <div class="invoice-meta">
              <div class="invoice-number">${orderNum}</div>
              <div class="order-barcode">
                <svg id="order-barcode"></svg>
              </div>
              <div class="meta-grid">
                <span class="meta-label">Invoice No.</span>
                <span class="meta-value">${orderNumClean}</span>
                <span class="meta-label">Order Date</span>
                <span class="meta-value">${orderDate}</span>
                ${shippingMethod ? `<span class="meta-label">Shipping</span><span class="meta-value">${shippingMethod}</span>` : ''}
              </div>
            </div>
          </div>

          <!-- Bill To / Ship To -->
          <div class="address-row">
            <div class="address-block">
              <div class="address-label">Bill To</div>
              <div class="address-name">${customerName}</div>
              ${shipAddr.map(line => `<div class="address-line">${line}</div>`).join('')}
              ${customerPhone ? `<div class="address-contact">${customerPhone}</div>` : ''}
              ${customerEmail ? `<div class="address-contact">${customerEmail}</div>` : ''}
            </div>
            <div class="address-block">
              <div class="address-label">Ship To</div>
              <div class="address-name">${customerName}</div>
              ${shipAddr.map(line => `<div class="address-line">${line}</div>`).join('')}
              ${customerPhone ? `<div class="address-contact">${customerPhone}</div>` : ''}
            </div>
          </div>

          <!-- Items Table -->
          <table class="items-table">
            <thead>
              <tr>
                <th>Item Description</th>
                <th style="text-align:center;">Qty</th>
                <th style="text-align:right;">Price</th>
                <th style="text-align:right;">Item Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemRows}
            </tbody>
          </table>

          <!-- Totals -->
          <div class="totals-section">
            <div class="totals-left">
              Total Number of Items: ${totalItems}
            </div>
            <div class="totals-table">
              <div class="row"><span class="label">Subtotal</span><span>${fmt(subtotal)}</span></div>
              ${discount > 0 ? `<div class="row"><span class="label">Discount</span><span>-${fmt(discount)}</span></div>` : `<div class="row"><span class="label">Discount</span><span>${fmt(0)}</span></div>`}
              <div class="row"><span class="label">Shipping</span><span>${fmt(shippingCost)}</span></div>
              <div class="row"><span class="label">Total Tax</span><span>${fmt(totalTax)}</span></div>
              <div class="row total"><span class="label">Total</span><span>${fmt(totalPrice)}</span></div>
            </div>
          </div>
          <div class="total-words">${numberToWords(totalPrice)}</div>

          <!-- Notes -->
          ${orderNotes ? `
          <div class="notes-section">
            <div class="notes-label">Order Notes</div>
            <div class="notes-text">${orderNotes}</div>
          </div>
          ` : ''}

          <!-- Footer -->
          <div class="invoice-footer">
            <div class="footer-thanks">Thank you for your business!</div>
            <div class="footer-note">If you have any questions, please do get in contact.</div>
            <div class="footer-company">www.peachstatecandlesupply.com</div>
          </div>

          <script>
            // Wait for JsBarcode to load, then render all barcodes
            function renderBarcodes() {
              try {
                // Order barcode (Code128)
                JsBarcode("#order-barcode", "${orderNumClean}", {
                  format: "CODE128",
                  width: 1.5,
                  height: 40,
                  displayValue: true,
                  fontSize: 11,
                  margin: 0,
                  textMargin: 2,
                });
              } catch(e) { console.warn("Order barcode error:", e); }

              // Item barcodes (try EAN13 for 13-digit, UPC for 12-digit, fall back to CODE128)
              document.querySelectorAll("[data-barcode]").forEach(function(el) {
                var code = el.getAttribute("data-barcode");
                if (!code) return;
                try {
                  var fmt = "CODE128";
                  var cleanCode = code.replace(/\\s/g, "");
                  if (/^\\d{12}$/.test(cleanCode)) fmt = "UPC";
                  else if (/^\\d{13}$/.test(cleanCode)) fmt = "EAN13";
                  JsBarcode(el, cleanCode, {
                    format: fmt,
                    width: 1.2,
                    height: 35,
                    displayValue: true,
                    fontSize: 10,
                    margin: 0,
                    textMargin: 2,
                  });
                } catch(e) {
                  // Fallback to CODE128 if specific format fails
                  try {
                    JsBarcode(el, code.replace(/\\s/g, ""), {
                      format: "CODE128",
                      width: 1.2,
                      height: 35,
                      displayValue: true,
                      fontSize: 10,
                      margin: 0,
                      textMargin: 2,
                    });
                  } catch(e2) { console.warn("Item barcode error:", e2); }
                }
              });
            }

            // Poll for JsBarcode to be available (CDN load)
            var bcInterval = setInterval(function() {
              if (typeof JsBarcode !== "undefined") {
                clearInterval(bcInterval);
                renderBarcodes();
              }
            }, 50);
            // Safety timeout
            setTimeout(function() { clearInterval(bcInterval); }, 5000);
          <\/script>

        </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    // Wait for barcodes to render before printing
    setTimeout(() => win.print(), 1200);
  };

  return (
    <div className="fixed inset-0 bg-white lg:bg-black lg:bg-opacity-50 flex lg:items-center lg:justify-center z-50 safe-px safe-pt safe-pb" onClick={handleClose}>
      <div
        className="bg-white w-full h-full lg:h-[90vh] max-h-screen lg:overflow-hidden lg:max-w-6xl lg:rounded-xl lg:m-4 lg:shadow-strong flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Minimal padding on mobile */}
        <div className="px-3 lg:px-8 py-1.5 lg:py-4 border-b border-slate-200 flex-shrink-0 bg-white">
          <div className="flex items-start justify-between gap-2 lg:gap-8">
            <div className="flex-1 lg:max-w-3xl">
              <h2 className="text-lg lg:text-2xl font-semibold text-slate-900">
                {/^[0-9]+$/.test(currentOrder.order_number) ? `#PSCS${currentOrder.order_number}` : currentOrder.order_number}
              </h2>
              {/* Mobile: Single line with name and location */}
              <p className="text-sm text-slate-500 lg:hidden">
                {currentOrder.first_name} {currentOrder.last_name} · {currentOrder.shipping_city}, {currentOrder.shipping_state}
              </p>
              {/* Desktop: Name + compact address */}
              <p className="text-sm text-slate-600 mt-0.5 hidden lg:block">
                {currentOrder.first_name} {currentOrder.last_name}
                {currentOrder.shipping_address_line1 && (
                  <span className="text-slate-400 ml-2">
                    — {currentOrder.shipping_city}, {currentOrder.shipping_state} {currentOrder.shipping_zip}
                    {addressVerified && <span className="ml-1.5 text-green-600 text-xs font-medium">✓ Verified</span>}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {((currentOrder as any).source === 'manual' || currentOrder.order_number?.startsWith('M-')) && currentOrder.status !== 'cancelled' && (
                <button
                  onClick={async () => {
                    // eslint-disable-next-line no-restricted-globals
                    if (!confirm('Are you sure you want to cancel this order?\n\nThis cannot be undone.')) return;
                    try {
                      await ordersApi.updateStatus(currentOrder.id, 'cancelled');
                      toast.success('Order Cancelled', `${currentOrder.order_number} has been cancelled.`);
                      onShip(); // Refresh orders list
                      handleClose();
                    } catch (error: any) {
                      toast.error('Failed to Cancel', error.response?.data?.error || error.message);
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Cancel Order
                </button>
              )}
              <button
                onClick={() => handlePrintOrder()}
                className="text-slate-400 hover:text-slate-600 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Print Order"
              >
                <PrinterIcon className="w-5 h-5" />
              </button>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-slate-600 text-2xl p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        {/* Blocked-address warning banner */}
        {currentOrder?.is_blocked && (
          <div className="bg-red-50 border-b-2 border-red-300 px-4 lg:px-8 py-3 flex-shrink-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
              <div className="flex items-start gap-3">
                <ShieldExclamationIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-900">⚠ This order matches your blocklist — review before shipping</div>
                  {currentOrder.blocked_reason && (
                    <div className="text-sm text-red-700 mt-0.5">{currentOrder.blocked_reason}</div>
                  )}
                </div>
              </div>
              <button
                onClick={handleUnblockOrder}
                className="text-xs font-medium text-red-700 bg-white border border-red-300 px-3 py-1.5 rounded-md hover:bg-red-100 self-start lg:self-auto"
              >
                Approve & clear flag
              </button>
            </div>
          </div>
        )}

        {/* Quick "Block this address" inline form */}
        {showBlockForm && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 lg:px-8 py-3 flex-shrink-0">
            <div className="flex flex-col lg:flex-row lg:items-center gap-2">
              <NoSymbolIcon className="w-5 h-5 text-amber-700 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-amber-900 mb-1">
                  Block this address & customer from future orders?
                </div>
                <input
                  type="text"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Reason (optional) — e.g. claimed item not received x2"
                  className="w-full px-2 py-1 text-sm border border-amber-300 rounded"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowBlockForm(false); setBlockReason(''); }}
                  disabled={blockSaving}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleQuickBlock}
                  disabled={blockSaving}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                >
                  {blockSaving ? 'Adding...' : 'Add to Blocklist'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* "Block address" trigger (only show if not already blocked & form not open) */}
        {!currentOrder?.is_blocked && !showBlockForm && currentOrder?.shipping_address_line1 && (
          <div className="px-4 lg:px-8 py-1.5 bg-slate-50 border-b border-slate-200 flex-shrink-0">
            <button
              onClick={() => setShowBlockForm(true)}
              className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
              title="Add this address to the blocklist for future orders"
            >
              <NoSymbolIcon className="w-3.5 h-3.5" />
              Block this address
            </button>
          </div>
        )}

        {/* Tabs - unified across contexts */}
        <div className="border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex justify-around lg:justify-start px-1 lg:px-8">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex-1 lg:flex-none px-2 lg:px-6 py-2 lg:py-3 text-xs lg:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'summary'
                  ? 'border-brand-blue text-brand-blue bg-white'
                  : 'border-transparent text-slate-600'
              }`}
            >
              <div className="flex items-center justify-center lg:justify-start gap-1.5 lg:gap-2">
                <ClipboardDocumentListIcon className="w-4 h-4" />
                <span>Summary</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('ship')}
              className={`flex-1 lg:flex-none px-2 lg:px-6 py-2 lg:py-3 text-xs lg:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'ship'
                  ? 'border-amber-600 text-amber-600 bg-white'
                  : 'border-transparent text-slate-600'
              }`}
            >
              <div className="flex items-center justify-center lg:justify-start gap-1.5 lg:gap-2">
                <MapPinIcon className="w-4 h-4" />
                <span>Ship</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('shipments')}
              className={`flex-1 lg:flex-none px-2 lg:px-6 py-2 lg:py-3 text-xs lg:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'shipments'
                  ? 'border-brand-blue text-brand-blue bg-white'
                  : 'border-transparent text-slate-600'
              }`}
            >
              <div className="flex items-center justify-center lg:justify-start gap-1.5 lg:gap-2">
                <TruckIcon className="w-4 h-4" />
                <span>Shipments</span>
                <span className="ml-1 bg-green-100 text-green-700 text-xs px-1.5 rounded-full">
                  {shipments.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 lg:flex-none px-2 lg:px-6 py-2 lg:py-3 text-xs lg:text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-slate-800 text-slate-800 bg-white'
                  : 'border-transparent text-slate-600'
              }`}
            >
              <div className="flex items-center justify-center lg:justify-start gap-1.5 lg:gap-2">
                <ClipboardDocumentListIcon className="w-4 h-4" />
                <span>History</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content - Ship tab uses flex/overflow-hidden so only inner panels scroll */}
        <div
          className={`flex-1 min-h-0 ${
            currentOrder?.order_type !== 'pickup' && activeTab === 'ship'
              ? 'overflow-hidden flex flex-col'
              : 'overflow-y-auto'
          }`}
        >
          {/* All items fulfilled, show summary with reship option */}
          {currentOrder?.order_type !== 'pickup' && activeTab === 'ship' && unfulfilledItems.length === 0 && !reshipMode && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">All Items Shipped</h3>
              <p className="text-sm text-slate-600 mb-4">
                This order has been fully fulfilled.
              </p>
              <div className="flex items-center justify-center mb-3">
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                  Reship mode will skip Shopify/Faire fulfillment
                </span>
              </div>
              <div className="flex items-center justify-center gap-3">
                {shipments.length > 0 && (
                  <button
                    onClick={() => setActiveTab('shipments')}
                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg"
                  >
                    View Tracking →
                  </button>
                )}
                <button
                  onClick={() => {
                    setReshipMode(true);
                    setManualSkipFulfillment(true); // default reship to skip fulfillment push
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg"
                >
                  Create Reship / New Shipment
                </button>
              </div>
            </div>
          )}

          {/* Summary Tab - All orders */}
          {activeTab === 'summary' && currentOrder && (
            <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
              {/* Customer & Shipping Info - Hidden on mobile (shown in header) */}
              <div className="hidden lg:grid grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Customer</h3>
                  <p className="text-sm font-medium text-slate-900">
                    {currentOrder.first_name} {currentOrder.last_name}
                  </p>
                  {currentOrder.customer_email && (
                    <p className="text-sm text-slate-600 mt-1">{currentOrder.customer_email}</p>
                  )}
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  {!isEditingAddress ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          Shipping Address
                          {addressVerified && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              ✓ Verified
                            </span>
                          )}
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditedAddress({
                                line1: currentOrder.shipping_address_line1 || '',
                                line2: currentOrder.shipping_address_line2 || '',
                                city: currentOrder.shipping_city || '',
                                state: currentOrder.shipping_state || '',
                                zip: currentOrder.shipping_zip || '',
                                country: currentOrder.shipping_country || 'US',
                              });
                              setIsEditingAddress(true);
                            }}
                            className="text-xs text-brand-blue hover:text-brand-blue-hover font-medium"
                          >
                            Edit Address
                          </button>
                          <button
                            onClick={handleResyncOrder}
                            disabled={isResyncing}
                            className="text-xs text-slate-500 hover:text-slate-700 font-medium disabled:opacity-50"
                            title="Re-sync from Shopify to update shipping method"
                          >
                            {isResyncing ? '↻ Syncing...' : '↻ Resync'}
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-slate-900 space-y-1">
                        {currentOrder.shipping_address_line1 && <p>{currentOrder.shipping_address_line1}</p>}
                        {currentOrder.shipping_address_line2 && <p>{currentOrder.shipping_address_line2}</p>}
                        <p>
                          {currentOrder.shipping_city}, {currentOrder.shipping_state} {currentOrder.shipping_zip}
                        </p>
                        <p>{getCountryName(currentOrder.shipping_country || 'US')}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-blue-900">Edit Shipping Address</h3>
                        <button
                          onClick={() => setIsEditingAddress(false)}
                          className="text-xs text-slate-600 hover:text-slate-900"
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editedAddress.line1}
                          onChange={(e) => setEditedAddress({ ...editedAddress, line1: e.target.value })}
                          placeholder="Address Line 1"
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded"
                        />
                        <input
                          type="text"
                          value={editedAddress.line2}
                          onChange={(e) => setEditedAddress({ ...editedAddress, line2: e.target.value })}
                          placeholder="Address Line 2 (Optional)"
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            value={editedAddress.city}
                            onChange={(e) => setEditedAddress({ ...editedAddress, city: e.target.value })}
                            placeholder="City"
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded"
                          />
                          {getRegionOptions(editedAddress.country) ? (
                            <select
                              value={editedAddress.state}
                              onChange={(e) => setEditedAddress({ ...editedAddress, state: e.target.value })}
                              className="w-full px-2 py-2 text-sm border border-slate-300 rounded bg-white"
                            >
                              <option value="">{getRegionLabel(editedAddress.country)}</option>
                              {getRegionOptions(editedAddress.country)!.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={editedAddress.state}
                              onChange={(e) => setEditedAddress({ ...editedAddress, state: e.target.value })}
                              placeholder="State/Region"
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded"
                            />
                          )}
                          <input
                            type="text"
                            value={editedAddress.zip}
                            onChange={(e) => setEditedAddress({ ...editedAddress, zip: e.target.value })}
                            placeholder={getPostalCodeLabel(editedAddress.country)}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded"
                          />
                        </div>
                        <select
                          value={editedAddress.country}
                          onChange={(e) => {
                            const country = e.target.value;
                            const options = getRegionOptions(country);
                            const stateStillValid = options ? options.includes(editedAddress.state) : true;
                            setEditedAddress({ ...editedAddress, country, state: stateStillValid ? editedAddress.state : '' });
                          }}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white"
                        >
                          {COUNTRIES.map(c => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                        <button
                          disabled={isVerifyingAddress}
                          onClick={async () => {
                            setIsVerifyingAddress(true);
                            try {
                              const updateResult = await ordersApi.updateAddress(order.id, {
                                address_line1: editedAddress.line1,
                                address_line2: editedAddress.line2,
                                city: editedAddress.city,
                                state: editedAddress.state,
                                zip: editedAddress.zip,
                                country: editedAddress.country || 'US',
                              });
                              const result = await ordersApi.get(order.id);
                              setRefreshedOrder({ ...result.order, items: result.items });
                              setIsEditingAddress(false);
                              if (updateResult.address_verified !== undefined) {
                                setAddressVerified(updateResult.address_verified);
                                if (updateResult.address_verified) {
                                  toast.success('Address Verified', 'Address saved and verified successfully');
                                } else {
                                  const messages = updateResult.validation_messages?.join(', ') || 'Address may have issues';
                                  toast.warning('Address Saved', `Address saved but verification warning: ${messages}`);
                                }
                              } else {
                                setAddressVerified(false);
                                toast.success('Address Updated', 'Shipping address has been updated');
                              }
                              if (employee) {
                                logActivity('address_updated', 'Shipping address edited from order modal');
                              }
                            } catch (error: any) {
                              toast.error('Update Failed', error.response?.data?.error || error.message);
                            } finally {
                              setIsVerifyingAddress(false);
                            }
                          }}
                          className="w-full px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isVerifyingAddress ? (
                            <>
                              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Verifying...
                            </>
                          ) : (
                            'Save & Verify Address'
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Items</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">SKU</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Qty</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {resolvedItems.map((item: any) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3">
                            <div className="flex items-center">
                              {item.image_url && (
                                <img src={item.image_url} alt={item.title} className="w-10 h-10 rounded mr-3" />
                              )}
                              <div>
                                <p className="text-sm text-slate-900">{item.title}</p>
                                {(item as any).variant_title && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    {(item as any).variant_title}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-mono text-slate-600">{item.sku || '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm text-slate-900">{item.quantity}</span>
                              {(!item.resolved_weight || item.resolved_weight <= 0) && (
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                  Weight missing
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-medium text-slate-900">
                              ${item.price ? parseFloat(item.price.toString()).toFixed(2) : '0.00'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      {(() => {
                        const shippingLines: any[] = (currentOrder as any).shipping_lines || [];
                        const shippingCharged = shippingLines.reduce((sum: number, line: any) => sum + parseFloat(line.price || '0'), 0);
                        const subtotal = (currentOrder as any).subtotal_price
                          ? parseFloat((currentOrder as any).subtotal_price.toString())
                          : resolvedItems.reduce((sum: number, item: any) => sum + (parseFloat(item.price || '0') * (item.quantity || 1)), 0);
                        const totalTax = (currentOrder as any).total_tax
                          ? parseFloat((currentOrder as any).total_tax.toString())
                          : 0;
                        const totalDiscounts = (currentOrder as any).total_discounts
                          ? parseFloat((currentOrder as any).total_discounts.toString())
                          : 0;
                        const total = currentOrder.total_price ? parseFloat(currentOrder.total_price.toString()) : 0;

                        return (
                          <>
                            <tr>
                              <td colSpan={3} className="px-4 pt-3 pb-1 text-right text-sm text-slate-600">
                                Subtotal
                              </td>
                              <td className="px-4 pt-3 pb-1 text-right text-sm text-slate-900">
                                ${subtotal.toFixed(2)}
                              </td>
                            </tr>
                            {totalDiscounts > 0 && (
                              <tr>
                                <td colSpan={3} className="px-4 py-1 text-right text-sm text-slate-600">
                                  Discount
                                </td>
                                <td className="px-4 py-1 text-right text-sm text-green-600">
                                  -${totalDiscounts.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td colSpan={3} className="px-4 py-1 text-right text-sm text-slate-600">
                                Shipping
                              </td>
                              <td className="px-4 py-1 text-right text-sm text-slate-900">
                                {shippingCharged > 0 ? `$${shippingCharged.toFixed(2)}` : 'Free'}
                              </td>
                            </tr>
                            {totalTax > 0 && (
                              <tr>
                                <td colSpan={3} className="px-4 py-1 text-right text-sm text-slate-600">
                                  Tax
                                </td>
                                <td className="px-4 py-1 text-right text-sm text-slate-900">
                                  ${totalTax.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            <tr className="border-t border-slate-200">
                              <td colSpan={3} className="px-4 pt-2 pb-3 text-right font-semibold text-slate-900">
                                Total
                              </td>
                              <td className="px-4 pt-2 pb-3 text-right font-semibold text-slate-900">
                                ${total.toFixed(2)}
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Ship Tab */}
          {currentOrder?.order_type === 'pickup' && activeTab === 'ship' && (
            <div className="flex flex-col h-full">
              {/* Amber Header Bar for Pickup */}
              <div className="bg-amber-600 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPinIcon className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">Local Pickup</span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                    currentOrder?.pickup_completed_at 
                      ? 'bg-green-500 text-white' 
                      : currentOrder?.pickup_ready 
                        ? 'bg-amber-300 text-amber-900' 
                        : 'bg-white/20 text-white'
                  }`}>
                    {currentOrder?.pickup_completed_at 
                      ? 'Picked Up' 
                      : currentOrder?.pickup_ready 
                        ? 'Ready for Pickup' 
                        : 'Awaiting Preparation'}
                  </span>
                </div>
                <span className="text-amber-100 text-sm">
                  {/^[0-9]+$/.test(currentOrder?.order_number || '') 
                    ? `#PSCS${currentOrder?.order_number}` 
                    : (currentOrder?.order_number?.startsWith('#') ? currentOrder?.order_number : `#${currentOrder?.order_number}`)}
                </span>
              </div>

              {/* Customer Info Bar */}
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{currentOrder?.first_name} {currentOrder?.last_name}</div>
                    {currentOrder?.customer_email && (
                      <div className="text-sm text-slate-600 flex items-center gap-1">
                        <EnvelopeIcon className="w-3.5 h-3.5" />
                        {currentOrder.customer_email}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Order Total</div>
                    <div className="font-bold text-slate-900">
                      ${currentOrder?.total_price ? parseFloat(currentOrder.total_price.toString()).toFixed(2) : '0.00'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons - At Top */}
              <div className="px-4 py-3 bg-white border-b border-slate-200">
                <div className="flex items-center justify-center gap-3">
                  {!currentOrder?.pickup_ready && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Mark this order as ready for customer pickup?\n\nThis will notify the customer that their order is ready.')) return;
                        try {
                          await ordersApi.markPickupReady(currentOrder.id);
                          toast.success('Ready for Pickup', 'Customer has been notified');
                          refreshOrderData();
                          onShip();
                        } catch (error: any) {
                          toast.error('Failed', error.response?.data?.error || error.message);
                        }
                      }}
                      className="px-6 py-3 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg flex items-center gap-2 shadow-sm"
                    >
                      <CheckIcon className="w-5 h-5" />
                      Mark Ready for Pickup
                    </button>
                  )}
                  
                  {currentOrder?.pickup_ready && !currentOrder?.pickup_completed_at && (
                    <>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Send a pickup reminder email to the customer?')) return;
                          try {
                            await ordersApi.sendPickupReminder(currentOrder.id);
                            toast.success('Reminder Sent', 'Customer has been reminded to pick up their order');
                          } catch (error: any) {
                            toast.error('Failed', error.response?.data?.error || error.message);
                          }
                        }}
                        className="px-4 py-3 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg flex items-center gap-2"
                      >
                        <EnvelopeIcon className="w-5 h-5" />
                        Send Reminder
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Mark this order as picked up by the customer?\n\nThis will complete the order fulfillment.')) return;
                          try {
                            await ordersApi.markPickupCompleted(currentOrder.id);
                            toast.success('Pickup Complete', 'Order has been marked as picked up');
                            refreshOrderData();
                            onShip();
                          } catch (error: any) {
                            toast.error('Failed', error.response?.data?.error || error.message);
                          }
                        }}
                        className="px-6 py-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-2 shadow-sm"
                      >
                        <CheckCircleIcon className="w-5 h-5" />
                        Mark Picked Up
                      </button>
                    </>
                  )}
                  
                  {currentOrder?.pickup_completed_at && (
                    <div className="flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 rounded-lg">
                      <CheckCircleIcon className="w-5 h-5" />
                      <span className="font-medium">Order Completed</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Timeline */}
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center justify-between max-w-sm mx-auto">
                  {/* Step 1: Order Received */}
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckCircleIcon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-xs text-slate-500 mt-1">Received</span>
                  </div>
                  <div className={`flex-1 h-0.5 mx-2 ${currentOrder?.pickup_ready ? 'bg-green-500' : 'bg-slate-300'}`} />
                  
                  {/* Step 2: Ready for Pickup */}
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      currentOrder?.pickup_ready ? 'bg-green-500' : 'bg-slate-300'
                    }`}>
                      {currentOrder?.pickup_ready ? (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      ) : (
                        <ClockIcon className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                    <span className="text-xs text-slate-500 mt-1">Ready</span>
                  </div>
                  <div className={`flex-1 h-0.5 mx-2 ${currentOrder?.pickup_completed_at ? 'bg-green-500' : 'bg-slate-300'}`} />
                  
                  {/* Step 3: Picked Up */}
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      currentOrder?.pickup_completed_at ? 'bg-green-500' : 'bg-slate-300'
                    }`}>
                      {currentOrder?.pickup_completed_at ? (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      ) : (
                        <MapPinIcon className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                    <span className="text-xs text-slate-500 mt-1">Picked Up</span>
                  </div>
                </div>
              </div>

              {/* Items Grid */}
              <div className="flex-1 overflow-y-auto">
                <div className="bg-slate-50 border-b border-slate-200">
                  <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    <div className="col-span-6">Product</div>
                    <div className="col-span-2 text-center">SKU</div>
                    <div className="col-span-2 text-center">Qty</div>
                    <div className="col-span-2 text-center">Status</div>
                  </div>
                </div>
                <div className="divide-y divide-slate-200">
                  {currentOrder?.items?.map((item: any) => (
                    <div key={item.id} className="grid grid-cols-12 gap-4 px-4 py-3 items-center bg-white hover:bg-slate-50">
                      <div className="col-span-6 flex items-center gap-3">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.title} className="w-10 h-10 rounded object-cover" />
                        )}
                        <div>
                          <div className="font-medium text-slate-900 text-sm">{item.title}</div>
                          {(item as any).variant_title && (
                            <div className="text-xs text-slate-500">{(item as any).variant_title}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="font-mono text-xs text-slate-600">{item.sku || '-'}</span>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="text-2xl font-bold text-slate-800">{item.quantity}</span>
                      </div>
                      <div className="col-span-2 text-center">
                        <div className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center ${
                          currentOrder?.pickup_completed_at 
                            ? 'bg-green-500' 
                            : currentOrder?.pickup_ready 
                              ? 'bg-amber-400' 
                              : 'bg-slate-200'
                        }`}>
                          {currentOrder?.pickup_completed_at ? (
                            <CheckIcon className="w-4 h-4 text-white" />
                          ) : currentOrder?.pickup_ready ? (
                            <CheckIcon className="w-4 h-4 text-white" />
                          ) : (
                            <ClockIcon className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timestamps */}
              {(currentOrder?.pickup_ready_at || currentOrder?.pickup_completed_at) && (
                <div className="bg-slate-50 border-t border-slate-200 px-4 py-2">
                  <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
                    {currentOrder?.pickup_ready_at && (
                      <span>
                        Ready: {new Date(currentOrder.pickup_ready_at).toLocaleDateString()} {new Date(currentOrder.pickup_ready_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    {currentOrder?.pickup_completed_at && (
                      <span className="text-green-600">
                        Picked up: {new Date(currentOrder.pickup_completed_at).toLocaleDateString()} {new Date(currentOrder.pickup_completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}
          {/* Ship Tab for shipping orders - manual + ShipEngine */}
          {currentOrder?.order_type !== 'pickup' && activeTab === 'ship' && (
            <div className="p-3 lg:p-4 space-y-2 lg:space-y-3 flex flex-col min-h-0 flex-1 overflow-hidden">
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
                <button
                  onClick={() => setShowManualShipment(!showManualShipment)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <TruckIcon className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Manual Shipment / Freight</span>
                    <span className="text-xs text-slate-400">— Add tracking without a label</span>
                  </div>
                  <svg className={`w-4 h-4 text-slate-400 transition-transform ${showManualShipment ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showManualShipment && (
                  <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                    <div className="flex items-center justify-end mb-3">
                      <button
                        disabled={manualSubmitting}
                        onClick={handleManualShipment}
                        className="px-3 py-2 text-xs font-semibold text-white bg-slate-800 rounded hover:bg-slate-900 disabled:opacity-60"
                      >
                        {manualSubmitting ? 'Saving...' : 'Save Manual Shipment'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Carrier</label>
                        <select
                          value={manualCarrier}
                          onChange={(e) => setManualCarrier(e.target.value)}
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        >
                          <option>UPS</option>
                          <option>USPS</option>
                          <option>FedEx</option>
                          <option>Freight/LTL</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Tracking Number</label>
                        <input
                          value={manualTracking}
                          onChange={(e) => setManualTracking(e.target.value)}
                          placeholder="1Z..."
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Cost (optional)</label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={manualCost}
                          onChange={(e) => setManualCost(e.target.value)}
                          placeholder="0.00"
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Notes</label>
                        <input
                          value={manualNotes}
                          onChange={(e) => setManualNotes(e.target.value)}
                          placeholder="Freight BOL, ref..."
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 items-center text-xs text-slate-600">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={manualIncludeItems && !manualSkipFulfillment}
                          disabled={manualSkipFulfillment}
                          onChange={(e) => setManualIncludeItems(e.target.checked)}
                        />
                        Include remaining items in fulfillment update
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={manualSkipFulfillment}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setManualSkipFulfillment(next);
                            if (next) setManualIncludeItems(false);
                          }}
                        />
                        Skip pushing fulfillment (replacement / reship, no Shopify/Faire push)
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {unfulfilledItems.length > 0 || reshipMode ? (
                <div className="flex-1 min-h-0 flex flex-col">
                  {reshipMode && unfulfilledItems.length === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 flex items-center justify-between flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-amber-600 text-sm font-medium">Reship Mode</span>
                        <span className="text-amber-700 text-xs">Creating additional shipment for this order (fulfillment will not be pushed to Shopify)</span>
                      </div>
                      <button
                        onClick={() => setReshipMode(false)}
                        className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <div className="border border-slate-200 rounded-lg overflow-hidden flex-1 min-h-0">
                    <UnifiedShippingView
                      order={{
                        ...currentOrder,
                        // In reship mode, use all items from the order (resolvedItems includes removed items)
                        items: reshipMode && unfulfilledItems.length === 0 
                          ? (resolvedItems || []).map((item: any) => ({ ...item, selected: true, weight_oz: item.resolved_weight || 0.1 }))
                          : unfulfilledItems,
                      }}
                      skipFulfillment={reshipMode && unfulfilledItems.length === 0}
                      onLabelCreated={() => {
                        loadShipments();
                        onShip();
                        if (reshipMode) setReshipMode(false);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4">
                  All items are fulfilled. You can still add manual shipments above for replacements or freight.
                </div>
              )}
            </div>
          )}

          {/* Shipments Tab - Redesigned */}
          {activeTab === 'shipments' && (
            <div className="flex flex-col h-full min-h-[480px]">
              {/* Dark Header Bar */}
              <div className="bg-slate-800 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TruckIcon className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">Shipment Tracking</span>
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded">
                    {shipments.length} Shipment{shipments.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowManualInShipments((v) => !v)}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-slate-700 hover:bg-slate-600 rounded"
                  >
                    {showManualInShipments ? 'Hide Manual Ship' : 'Add Manual Shipment'}
                  </button>
                  <span className="text-slate-300 text-sm">
                    {/^[0-9]+$/.test(currentOrder?.order_number || '') 
                      ? `#PSCS${currentOrder?.order_number}` 
                      : (currentOrder?.order_number?.startsWith('#') ? currentOrder?.order_number : `#${currentOrder?.order_number}`)}
                  </span>
                </div>
              </div>

              {/* Order Info Bar */}
              <div className="bg-slate-100 border-b border-slate-200 px-4 py-2 flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className="text-slate-600">
                    <span className="font-medium text-slate-800">{currentOrder?.first_name} {currentOrder?.last_name}</span>
                  </span>
                  {currentOrder?.shipping_city && (
                    <span className="text-slate-500">
                      → {currentOrder.shipping_city}, {currentOrder.shipping_state} {currentOrder.shipping_zip}
                    </span>
                  )}
                </div>
                <span className="text-slate-600">
                  Total: <span className="font-semibold text-slate-800">${currentOrder?.total_price ? parseFloat(currentOrder.total_price.toString()).toFixed(2) : '0.00'}</span>
                </span>
              </div>

          {/* Shipments List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[320px]">
                {showManualInShipments && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <TruckIcon className="w-5 h-5 text-slate-700" />
                        <div>
                          <h3 className="text-sm font-semibold text-slate-800">Manual Shipment / Freight</h3>
                          <p className="text-xs text-slate-500">Add tracking without creating a ShipEngine label.</p>
                        </div>
                      </div>
                      <button
                        disabled={manualSubmitting}
                        onClick={handleManualShipment}
                        className="px-3 py-2 text-xs font-semibold text-white bg-slate-800 rounded hover:bg-slate-900 disabled:opacity-60"
                      >
                        {manualSubmitting ? 'Saving...' : 'Save Manual Shipment'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Carrier</label>
                        <select
                          value={manualCarrier}
                          onChange={(e) => setManualCarrier(e.target.value)}
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        >
                          <option>UPS</option>
                          <option>USPS</option>
                          <option>FedEx</option>
                          <option>Freight/LTL</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Tracking Number</label>
                        <input
                          value={manualTracking}
                          onChange={(e) => setManualTracking(e.target.value)}
                          placeholder="1Z..."
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Cost (optional)</label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={manualCost}
                          onChange={(e) => setManualCost(e.target.value)}
                          placeholder="0.00"
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-600">Notes</label>
                        <input
                          value={manualNotes}
                          onChange={(e) => setManualNotes(e.target.value)}
                          placeholder="Freight BOL, ref..."
                          className="border border-slate-300 rounded px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 items-center text-xs text-slate-600">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={manualIncludeItems && !manualSkipFulfillment}
                          disabled={manualSkipFulfillment}
                          onChange={(e) => setManualIncludeItems(e.target.checked)}
                        />
                        Include remaining items in fulfillment update
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={manualSkipFulfillment}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setManualSkipFulfillment(next);
                            if (next) setManualIncludeItems(false);
                          }}
                        />
                        Skip pushing fulfillment (replacement / reship, no Shopify/Faire push)
                      </label>
                    </div>
                  </div>
                )}
                {loadingShipments ? (
                  <div className="space-y-3">
                    <div className="h-5 w-32 bg-slate-100 animate-pulse rounded" />
                    <div className="h-24 w-full bg-slate-100 animate-pulse rounded" />
                    <div className="h-24 w-full bg-slate-100 animate-pulse rounded" />
                  </div>
                ) : shipments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No shipments found</div>
                ) : (
                  shipments.map((shipment, index) => {
                    const claim = shipmentClaims[shipment.id];
                    const trackingNumber = shipment.tracking_number || shipment.packages?.[0]?.tracking_number;
                    const carrierLower = shipment.carrier?.toLowerCase() || '';
                    
                    // Build tracking URL
                    let trackingUrl = '';
                    if (trackingNumber) {
                      if (carrierLower.includes('ups')) {
                        trackingUrl = `https://www.ups.com/track?tracknum=${trackingNumber}`;
                      } else if (carrierLower.includes('usps')) {
                        trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
                      } else if (carrierLower.includes('fedex')) {
                        trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
                      } else if (carrierLower.includes('dhl')) {
                        trackingUrl = `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`;
                      }
                    }

                    return (
                      <div key={shipment.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        {/* Shipment Header */}
                        <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CarrierLogo carrier={shipment.carrier} size="sm" />
                            <div>
                              <div className="font-semibold text-slate-900">{shipment.carrier?.toUpperCase()}</div>
                              <div className="text-xs text-slate-500">{shipment.service_level}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {shipment.is_return && (
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded flex items-center gap-1">
                                <ArrowUturnLeftIcon className="w-3 h-3" />
                                Return
                              </span>
                            )}
                            {shipment.is_international && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded flex items-center gap-1">
                                <GlobeAltIcon className="w-3 h-3" />
                                International
                              </span>
                            )}
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              shipment.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              shipment.status === 'delivered' ? 'bg-blue-100 text-blue-700' :
                              shipment.status === 'in_transit' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {shipment.status === 'confirmed' ? 'Shipped' :
                               shipment.status === 'delivered' ? 'Delivered' :
                               shipment.status === 'in_transit' ? 'In Transit' :
                               shipment.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Pending'}
                            </span>
                          </div>
                        </div>

                        {/* Shipment Details */}
                        <div className="p-4 space-y-3">
                          {/* Tracking Number */}
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Tracking Number</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-slate-900">{trackingNumber || '-'}</span>
                              {trackingUrl && (
                                <a
                                  href={trackingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-blue hover:text-brand-blue-hover"
                                  title="Track package"
                                >
                                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Cost */}
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Shipping Cost</span>
                            <span className="text-sm font-semibold text-slate-900">
                              ${parseFloat(shipment.cost?.toString() || '0').toFixed(2)}
                            </span>
                          </div>

                          {/* Date */}
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Created</span>
                            <span className="text-sm text-slate-700">
                              {new Date(shipment.created_at).toLocaleDateString()} {new Date(shipment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>

                          {/* Packages */}
                          {shipment.packages && shipment.packages.length > 0 && (
                            <div className="pt-2 border-t border-slate-100">
                              <div className="text-xs font-semibold text-slate-500 uppercase mb-2">
                                Packages ({shipment.packages.length})
                              </div>
                              <div className="space-y-2">
                                {shipment.packages.map((pkg: any, pkgIndex: number) => (
                                  <div
                                    key={pkg.id || pkgIndex}
                                    className="text-sm text-slate-600 flex items-center justify-between bg-slate-50 px-3 py-2 rounded"
                                  >
                                    <div className="flex flex-col">
                                      <span>Package {pkgIndex + 1}</span>
                                      <span className="text-xs text-slate-500">
                                        {pkg.weight ? `${formatWeight(pkg.weight)} • ` : ''}
                                        {pkg.length && pkg.width && pkg.height ? `${pkg.length}x${pkg.width}x${pkg.height} • ` : ''}
                                        {pkg.tracking_number || trackingNumber}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {pkg.customs_form_url && (
                                        <a
                                          href={pkg.customs_form_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-lg flex items-center gap-1"
                                        >
                                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                          Customs Doc
                                        </a>
                                      )}
                                      {pkg.label_data && (
                                        <button
                                          onClick={() => openLabel(pkg.label_data)}
                                          className="px-3 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-lg flex items-center gap-1"
                                        >
                                          <PrinterIcon className="w-3.5 h-3.5" />
                                          Print Label
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Claim Status */}
                          {claim && (
                            <div className="pt-2 border-t border-slate-100">
                              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-amber-800">
                                    Issue Reported: {claim.claim_type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                  </div>
                                  <div className="text-xs text-amber-600">
                                    Status: {claim.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-wrap gap-2">
                          {trackingUrl && (
                            <a
                              href={trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 text-xs font-medium text-brand-blue bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1"
                            >
                              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                              Track
                            </a>
                          )}
                          {shipment.packages?.[0]?.label_data && (
                            <button
                              onClick={() => printAllLabels(shipment.packages)}
                              className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-lg flex items-center gap-1"
                            >
                              <PrinterIcon className="w-3.5 h-3.5" />
                              Print All Labels
                            </button>
                          )}
                          {shipment.packages?.some((pkg: any) => !!pkg.customs_form_url) && (
                            <button
                              onClick={() => openCustomsDocs(shipment.packages)}
                              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-lg flex items-center gap-1"
                            >
                              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                              Print Customs Docs
                            </button>
                          )}
                          {!shipment.is_return && !shipment.is_international && shipment.packages?.some((p: any) => p.shipengine_label_id) && shipment.status !== 'voided' && (
                            <button
                              onClick={() => handleOpenReturnModal(shipment)}
                              className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg flex items-center gap-1"
                            >
                              <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                              Return Label
                            </button>
                          )}
                          {!claim && (
                            <button
                              onClick={() => handleOpenClaimModal(shipment)}
                              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg flex items-center gap-1"
                            >
                              <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                              Report Issue
                            </button>
                          )}
                          {shipment.status !== 'voided' && (
                            <button
                              onClick={() => handleVoidShipment(shipment.id)}
                              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg flex items-center gap-1"
                            >
                              <XMarkIcon className="w-3.5 h-3.5" />
                              Void
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-6 text-sm text-slate-600">
                  <span>
                    Order Total: <span className="font-semibold text-slate-900">
                      ${currentOrder?.total_price ? parseFloat(currentOrder.total_price.toString()).toFixed(2) : '0.00'}
                    </span>
                  </span>
                  <span>
                    Shipping Cost: <span className="font-semibold text-slate-900">
                      ${shipments.reduce((sum, s) => sum + parseFloat(s.cost?.toString() || '0'), 0).toFixed(2)}
                    </span>
                  </span>
                </div>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>
          )}
          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="p-4 lg:p-6 min-h-[320px]">
              {loadingActivity ? (
                <div className="p-4 text-center text-sm text-slate-500">Loading activity...</div>
              ) : activityLog.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-500">No activity recorded yet</div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="max-h-[420px] overflow-y-auto">
                    <table className="min-w-full">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Action</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Employee</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {activityLog.map((activity: any) => (
                          <tr key={activity.id} className="hover:bg-slate-50">
                            <td className="px-3 py-2">
                              <div className="text-sm text-slate-900">{formatActionType(activity.action_type)}</div>
                              {activity.description && (
                                <div className="text-xs text-slate-500 truncate max-w-xs">{activity.description}</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm text-slate-600">
                              {activity.employee_first_name} {activity.employee_last_name}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500">
                              {new Date(activity.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Shipment Tab Footer - Only for pickup orders */}
          {currentOrder?.order_type === 'pickup' && activeTab === 'ship' && (
            <div className="flex-shrink-0 border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Split Quantity Modal */}
      {splittingItem && (
        <SplitQuantityModal
          item={splittingItem}
          boxes={boxes}
          onSplit={(splits, newBoxQty) => handleSplitQuantity(splittingItem, splits, newBoxQty)}
          onAddNewBox={(quantity) => handleSplitToNewBox(splittingItem, quantity)}
          onQuickSplit={(qtyPerBox, dimensions) => handleQuickSplit(splittingItem, qtyPerBox, dimensions)}
          onCancel={() => setSplittingItem(null)}
        />
      )}

      {/* Return Label Modal */}
      {returnModalShipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setReturnModalShipment(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowUturnLeftIcon className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-semibold text-slate-900">Create Return Label</h3>
              </div>
              <button onClick={() => setReturnModalShipment(null)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">Select items to return</div>
                <div className="space-y-2">
                  {returnItems.map((item, idx) => (
                    <div key={item.order_item_id} className="flex items-center gap-3 bg-slate-50 px-3 py-2 rounded-lg">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={(e) => setReturnItems(prev => prev.map((it, i) => i === idx ? { ...it, selected: e.target.checked } : it))}
                        className="rounded border-slate-300"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{item.title}</div>
                        <div className="text-xs text-slate-500">
                          {item.sku && <span>SKU: {item.sku} · </span>}
                          {item.unitWeight > 0 ? formatWeight(item.unitWeight) + '/ea' : 'No weight'}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setReturnItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, it.quantity - 1) } : it))}
                          disabled={!item.selected || item.quantity <= 1}
                          className="w-6 h-6 flex items-center justify-center text-xs border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30"
                        >-</button>
                        <span className="w-8 text-center text-sm font-medium">{item.selected ? item.quantity : 0}</span>
                        <button
                          onClick={() => setReturnItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: Math.min(it.maxQty, it.quantity + 1) } : it))}
                          disabled={!item.selected || item.quantity >= item.maxQty}
                          className="w-6 h-6 flex items-center justify-center text-xs border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-30"
                        >+</button>
                        <span className="text-xs text-slate-400 w-10 text-right">/ {item.maxQty}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Weight</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min={0} value={returnDisplayLb} onChange={(e) => { setReturnWeightLb(parseInt(e.target.value) || 0); if (returnWeightOz === null) setReturnWeightOz(returnDisplayOz); }} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm text-right" />
                    <span className="text-xs text-slate-500">lb</span>
                    <input type="number" min={0} max={15} value={returnDisplayOz} onChange={(e) => { setReturnWeightOz(parseInt(e.target.value) || 0); if (returnWeightLb === null) setReturnWeightLb(returnDisplayLb); }} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm text-right" />
                    <span className="text-xs text-slate-500">oz</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Dimensions (L x W x H)</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} value={returnDims.length} onChange={(e) => setReturnDims(d => ({ ...d, length: parseFloat(e.target.value) || 0 }))} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm text-right" />
                    <span className="text-xs text-slate-400">x</span>
                    <input type="number" min={1} value={returnDims.width} onChange={(e) => setReturnDims(d => ({ ...d, width: parseFloat(e.target.value) || 0 }))} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm text-right" />
                    <span className="text-xs text-slate-400">x</span>
                    <input type="number" min={1} value={returnDims.height} onChange={(e) => setReturnDims(d => ({ ...d, height: parseFloat(e.target.value) || 0 }))} className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm text-right" />
                    <span className="text-xs text-slate-500">in</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl space-y-2">
              {returnOverMaxWeight && (
                <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  Exceeds max package weight of {formatWeight(maxPackageWeightOz)}. Reduce quantity and create separate labels.
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  {returnItems.filter(i => i.selected).length} item{returnItems.filter(i => i.selected).length !== 1 ? 's' : ''} · {returnTotalWeight > 0 ? formatWeight(returnTotalWeight) : 'No weight'}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setReturnModalShipment(null)} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitReturnLabel}
                    disabled={creatingReturnLabel === returnModalShipment.id || returnItems.filter(i => i.selected).length === 0 || returnOverMaxWeight}
                    className="px-4 py-2 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
                  >
                  {creatingReturnLabel === returnModalShipment.id ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <ArrowUturnLeftIcon className="w-4 h-4" />
                      Create Return Label
                    </>
                  )}
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Issue / Claim Modal */}
      {showClaimModal && claimShipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowClaimModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Report Shipping Issue</h3>
                  <p className="text-sm text-slate-600">
                    Tracking: {claimShipment.tracking_number || claimShipment.packages?.[0]?.tracking_number}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Issue Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Issue Type</label>
                <select
                  value={claimType}
                  onChange={(e) => setClaimType(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                >
                  <option value="lost">Package Lost - Never arrived, no tracking updates</option>
                  <option value="damaged">Package Damaged - Contents damaged on arrival</option>
                  <option value="not_delivered">Not Delivered - Shows delivered but not received</option>
                  <option value="wrong_address">Wrong Address - Delivered to wrong location</option>
                  <option value="other">Other Issue</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description (Optional)</label>
                <textarea
                  value={claimDescription}
                  onChange={(e) => setClaimDescription(e.target.value)}
                  placeholder="Describe the issue..."
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none"
                />
              </div>

              {/* Claim Amount */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Claim Amount ($)</label>
                <input
                  type="number"
                  value={claimAmount}
                  onChange={(e) => setClaimAmount(e.target.value)}
                  placeholder="Order total"
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the value to claim (default: order total)
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Next Steps:</strong> After creating this claim record, you may need to file directly with {claimShipment.carrier || 'the carrier'}.
                  We'll provide a link to their claim portal.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => setShowClaimModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitClaim}
                disabled={submittingClaim}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {submittingClaim ? 'Creating...' : 'Create Claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
